import {
  type CSSProperties,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  getProductDetailPath,
  Button,
  Category,
  formatProductUsdPrice,
  getCategoryPathFromId,
  getCategorySecondaryImageUrl,
  getProductUnitLabel,
  normalizeCategorySecondaryImageKey,
} from "@b2b/ui-components";
import {
  HOME_PRODUCT_CATEGORY_DISPLAY,
  getHomeProductCategoryDisplayImageUrl,
} from "../../config/homeProductCategories";
import {
  adminService,
  favoritesService,
  homepageService,
  productService,
} from "@b2b/services";
import { useAuth } from "../../auth/Authprovider";
import { BizcoverIndiaFigmaHeader } from "../../components/marketing/BizcoverIndiaFigmaHeader";
import { BizcoverIndiaFigmaFooter } from "../../components/marketing/BizcoverIndiaFigmaFooter";
import bannerImage from "../../assets/banner.png";
import { HOME_HERO_BANNER_IMAGE_URL } from "../../config/homeHeroBanner";
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Eye,
  Heart,
  MoveUpRightIcon,
} from "lucide-react";

function getSafePrice(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const cleaned = value.replace(/[^0-9.]/g, "");
    const parsed = Number.parseFloat(cleaned);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
}

const OUR_PRODUCTS_ITEMS = Array.from({ length: 10 }, (_, i) => ({
  id: `our-product-${i + 1}`,
  name: "VitaLife Omega-3 Softgels Heart Support Max Strength",
  category: "Support Max Strength",
  price: "$27.49",
  oldPrice: "$29.99",
}));

type ProductCardItem = {
  id: string;
  name: string;
  categoryId?: string;
  category: string;
  price: string;
  oldPrice: string;
  /** Unit of measure label, e.g. "pieces" — shown as "Per {unitLabel}" under price. */
  unitLabel?: string;
  imageUrl?: string;
  brand?: string;
};

function ProductCardPerUnitLine({ unitLabel }: { unitLabel?: string }) {
  if (!unitLabel) return null;
  return (
    <p className="mt-0.5 text-xs text-[#637381]">
      <span className="text-[#919eab]">Per </span>
      {unitLabel}
    </p>
  );
}

type FavoriteCardProps = {
  isFavorited?: boolean;
  isFavoriteLoading?: boolean;
  onToggleFavorite?: (id: string, isFavorited: boolean) => void;
};

const DEALS_PAGE_SIZE = 4;
const BEAUTY_PAGE_SIZE = 5;
/** Auto-advance paginated product carousels (New Arrivals + industry sections). */
const HOME_PRODUCT_CAROUSEL_AUTO_MS = 4800;
const CATEGORY_SLIDER_PAGE_SIZE = 8;
/** Product Categories carousel: second-level categories ranked by product count. */
const HOME_PRODUCT_CATEGORY_SLIDE_LIMIT = 20;
/** Carousel nav — same treatment as product details “Relevance Products” arrows. */
const HOME_SECTION_NAV_BTN =
  "inline-flex items-center justify-center rounded-full border border-[#F97316]/45 bg-[#fff7ed] text-[#F97316] transition-all duration-200 hover:scale-110 hover:border-[#F97316] hover:bg-[#F97316] hover:text-white active:scale-95 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:scale-100 disabled:hover:border-[#F97316]/45 disabled:hover:bg-[#fff7ed] disabled:hover:text-[#F97316]";
const HOME_SECTION_NAV_BTN_MD = `${HOME_SECTION_NAV_BTN} h-9 w-9`;
const HOME_SECTION_NAV_BTN_SM = `${HOME_SECTION_NAV_BTN} h-8 w-8`;
const HOME_SECTION_NAV_BTN_XS = `${HOME_SECTION_NAV_BTN} h-7 w-7`;
const HIDE_BANNER_TEXT_CONTENT = true;
/** CMS / default hero art (760×608) — keeps banner height consistent across browsers. */
const HOME_TOP_BANNER_ASPECT_CLASS = "aspect-[760/608]";
/** Set to true to show the Best Selling and Featured Products sections again. */
const SHOW_HOME_FEATURED_AND_BEST_SELLING = false;
/** Set to true to show the middle promo banner tiles (BeautyFeature cards) again. */
const SHOW_HOME_MIDDLE_BANNERS = false;

/** Match New Arrivals carousel depth so industry rows get multiple pages + auto-advance. */
const HOME_INDUSTRY_PRODUCT_LIMIT = 30;
/** Fetch extra category products so we can rank by membership + verification before slicing. */
const HOME_INDUSTRY_FETCH_LIMIT = 40;
/** Top-level industry rows: slug candidates must match `Category.slug` from the catalog (or API slug fallback). */
const HOME_INDUSTRY_SLUG_ROWS: { rowKey: string; slugCandidates: string[] }[] =
  [
    { rowKey: "agriculture", slugCandidates: ["agriculture"] },
    {
      rowKey: "food_&_beverage",
      slugCandidates: [
        "food_&_beverage",
        "food-and-beverage",
        "food_beverage",
        "food-&-beverage",
      ],
    },
    { rowKey: "grain", slugCandidates: ["grain", "grains"] },
    {
      rowKey: "tiles_&_accessories",
      slugCandidates: [
        "tiles_&_accessories",
        "tiles-and-accessories",
        "tiles_accessories",
        "tiles-&-accessories",
      ],
    },
  ];

const TOP_PROMOS = [
  {
    title: "Your Daily Store.",
    subtitle: "Essentials, deals and more.",
    bg: "#EAF2FF",
    cta: "Shop Now",
  },
  {
    title: "Everyday Made Simple.",
    subtitle: "Quick, easy and effortless shopping.",
    bg: "#E9F8F3",
    cta: "Shop Now",
  },
  {
    title: "Your Cart. Your Way.",
    subtitle: "All your favorites in one click.",
    bg: "#F1EEFF",
    cta: "Shop Now",
  },
];

const TOP_PROMO_BG_COLORS = ["#EAF2FF", "#E9F8F3", "#F1EEFF"] as const;
const EASE_OUT: [number, number, number, number] = [0.22, 1, 0.36, 1];
/** One calm fade for product grids (no stagger, no directional drift) */
const PRODUCT_STAGGER_CONTAINER = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0,
      delayChildren: 0,
    },
  },
};
const PRODUCT_STAGGER_ITEM = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { duration: 0.38, ease: EASE_OUT },
  },
};
const SECTION_REVEAL = {
  initial: { opacity: 0 },
  whileInView: { opacity: 1 },
  viewport: { once: true, amount: 0.12 },
  transition: { duration: 0.42, ease: EASE_OUT },
} as const;
const BEAUTY_FEATURE_TILES = [
  {
    id: "beauty-feature-1",
    title: "Your Daily Store.",
    subtitle: "Essentials, deals, and more.",
    cta: "Shop Now",
    accent: "bg-[#FFE8D1]",
  },
  {
    id: "beauty-feature-2",
    title: "Everyday Made Simple.",
    subtitle: "Quick, easy and effortless shopping.",
    cta: "Shop Now",
    accent: "bg-[#FFF1E0]",
  },
  {
    id: "beauty-feature-3",
    title: "Your Cart. Your Way.",
    subtitle: "All your favorites, in one click.",
    cta: "Shop Now",
    accent: "bg-[#FFE0CC]",
  },
  {
    id: "beauty-feature-4",
    title: "Unveil Your Natural Glow",
    subtitle:
      "Transform your skincare routine with our exclusive collection for radiant results.",
    cta: "View All Products",
    accent: "bg-[#FFD9B8]",
  },
  {
    id: "beauty-feature-5",
    title: "Premium Luxury Glow Picks",
    subtitle:
      "Upgrade your routine with our premium skincare and beauty essentials.",
    cta: "Shop Now",
    accent: "bg-[#FFEFDB]",
  },
] as const;
const BOTTOM_BANNER_ACCENTS = [
  "#EAF2FF",
  "#E9F8F3",
  "#F1EEFF",
  "#E7F3FF",
  "#EEF6FF",
] as const;

/** Shared layered elevation for the themed homepage section cards.
 *  Layered shadow gives soft depth without being heavy. */
const HOME_SECTION_SHADOW =
  "0 14px 36px -12px rgba(15, 23, 42, 0.22), 0 6px 14px -6px rgba(15, 23, 42, 0.10)";

/** Product Categories section — palette derived from base plum (#4d376e). */
const SHOP_BY_CATEGORY_BASE = "#4d376e";
/** Section fill — base plum. */
const SHOP_BY_CATEGORY_BG = SHOP_BY_CATEGORY_BASE;
/** Light text on the dark section. */
const SHOP_BY_CATEGORY_HEADING = "#FFFFFF";
const SHOP_BY_CATEGORY_SUBTITLE = "#E8E3EF";
const SHOP_BY_CATEGORY_LABEL = "#FFFFFF";

/** Background color for New Arrivals section. */
const NEW_ARRIVALS_BG = "#FFDCD8";
/** Soft tint of the section bg used for the top-left page-peel notch so the
 *  curve does not sharply contrast against the inner bg. */
const NEW_ARRIVALS_NOTCH = "#FFEFEC";
/** Heading and subtitle colors — darker variants of the section hue for
 *  contrast against the notch tint they sit on. */
const NEW_ARRIVALS_HEADING = "#7A2A22";
const NEW_ARRIVALS_SUBTITLE = "#A8483F";

/** Shared compact header layout for home product carousel sections. */
const HOME_CAROUSEL_SECTION_MT = "mt-8";
const HOME_CAROUSEL_SECTION_SHELL =
  "relative min-h-0 overflow-hidden rounded-[48px] px-4 pb-4 pt-[108px] sm:px-5 sm:pb-4 sm:pt-[100px] md:px-6 md:pb-4 md:pt-[96px]";
const HOME_CAROUSEL_SECTION_SHELL_COMPACT_PB =
  "relative overflow-hidden rounded-[48px] px-4 pb-3 pt-[108px] sm:px-5 sm:pb-4 sm:pt-[100px] md:px-6 md:pb-4 md:pt-[96px]";
const HOME_CAROUSEL_PRODUCT_GRID_CLASS =
  "relative z-[2] grid grid-cols-1 items-stretch gap-3 pb-0 md:grid-cols-3 xl:grid-cols-5";
const HOME_CAROUSEL_NOTCH_CLASS =
  "pointer-events-none absolute left-0 top-0 z-[1] h-[80px] w-[260px] sm:h-[84px] sm:w-[320px] md:h-[88px] md:w-[400px]";
const HOME_CAROUSEL_NOTCH_RADIUS = "72px";
const HOME_CAROUSEL_HEADER_POS =
  "absolute left-5 top-3 z-[2] sm:left-6 md:left-7";
const HOME_CAROUSEL_HEADER_POS_WIDE =
  "absolute left-5 top-3 z-[2] max-w-[min(100%,520px)] pr-28 sm:left-6 md:left-7";
const HOME_CAROUSEL_NAV_POS =
  "absolute right-4 top-3 z-[2] flex items-center gap-2 sm:right-5 md:right-6";
const HOME_CAROUSEL_TITLE_CLASS =
  "text-[26px] font-bold leading-[32px] md:text-[30px] md:leading-[36px]";
const HOME_CAROUSEL_SUBTITLE_CLASS = "mt-0.5 text-sm";
const HOME_GRID_NOTCH_CLASS =
  "pointer-events-none absolute left-0 top-0 z-[1] h-[clamp(70px,9.5vw,92px)] w-[min(100%,400px)] max-w-[62%] bg-[#f8fbff] sm:max-w-[58%] md:w-[min(100%,420px)] md:max-w-[52%]";
const HOME_GRID_INNER_PAD =
  "relative z-[2] px-4 pb-4 pt-4 sm:px-6 sm:pb-5 sm:pt-5 md:px-8 md:pb-5 md:pt-5";

/** Per-industry-row background color, keyed by HOME_INDUSTRY_SLUG_ROWS.rowKey.
 *  Currently unified to the New Arrivals peach palette across all rows. */
const INDUSTRY_ROW_BG: Record<string, string> = {
  agriculture: "#FFDCD8",
  "food_&_beverage": "#FFDCD8",
  grain: "#FFDCD8",
  "tiles_&_accessories": "#FFDCD8",
};
/** Matching soft tints for the page-peel notch on each industry row. */
const INDUSTRY_ROW_NOTCH: Record<string, string> = {
  agriculture: "#FFEFEC",
  "food_&_beverage": "#FFEFEC",
  grain: "#FFEFEC",
  "tiles_&_accessories": "#FFEFEC",
};
/** Darker variants of each row's section hue for heading text. */
const INDUSTRY_ROW_HEADING: Record<string, string> = {
  agriculture: "#7A2A22",
  "food_&_beverage": "#7A2A22",
  grain: "#7A2A22",
  "tiles_&_accessories": "#7A2A22",
};
/** Medium variants for subtitle text. */
const INDUSTRY_ROW_SUBTITLE: Record<string, string> = {
  agriculture: "#A8483F",
  "food_&_beverage": "#A8483F",
  grain: "#A8483F",
  "tiles_&_accessories": "#A8483F",
};
/** Per-row marquee animation config — gives each industry section a unique
 *  scroll tempo and direction. `secondsPerProduct` multiplies the product
 *  count to compute the loop duration (higher = slower). */
const INDUSTRY_MARQUEE_CONFIG: Record<
  string,
  { secondsPerProduct: number; reverse: boolean }
> = {
  agriculture: { secondsPerProduct: 12, reverse: false },
  "food_&_beverage": { secondsPerProduct: 10, reverse: true },
  grain: { secondsPerProduct: 8, reverse: false },
  "tiles_&_accessories": { secondsPerProduct: 14, reverse: true },
};

type TopPromoItem = {
  id: string;
  title: string;
  subtitle: string;
  bg: string;
  cta: string;
  imageUrl?: string;
  ctaUrl?: string;
};
type BeautyFeatureItem = {
  id: string;
  title: string;
  subtitle: string;
  cta: string;
  accentColor: string;
  imageUrl?: string;
  ctaUrl?: string;
};
type ManagedHomepageSection = {
  id: string;
  title: string;
  description: string;
  publicSlug: string;
  placement: "TOP" | "BOTTOM";
  sectionType: "HORIZONTAL_SCROLL" | "GRID";
  bannerAccentColor: string;
  sourceConfig: {
    leftPanel?: { imageUrl?: string; title?: string; description?: string };
    sectionOneTitle?: string;
    sectionTwoTitle?: string;
    sectionOneProductIds?: string[];
    sectionTwoProductIds?: string[];
  } | null;
  products: ProductCardItem[];
  gridSectionOneProducts?: ProductCardItem[];
  gridSectionTwoProducts?: ProductCardItem[];
  gridSectionOneTitle?: string;
  gridSectionTwoTitle?: string;
  gridLeftPanel?: { imageUrl?: string; title?: string; description?: string };
};

type HomeIndustryShowcaseRow = {
  rowKey: string;
  title: string;
  /** Resolved category id for navigation and product queries (may be inferred from first product). */
  navigateCategoryId: string | null;
  products: ProductCardItem[];
};

const HOME_CACHE_VERSION = "v2";
const HOME_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const homeCache = {
  read<T>(key: string): T | null {
    try {
      if (typeof window === "undefined") return null;
      const raw = window.localStorage.getItem(
        `home_${HOME_CACHE_VERSION}::${key}`,
      );
      if (!raw) return null;
      const parsed = JSON.parse(raw) as { ts?: number; data?: T };
      if (!parsed || typeof parsed.ts !== "number") return null;
      if (Date.now() - parsed.ts > HOME_CACHE_TTL_MS) return null;
      return (parsed.data ?? null) as T | null;
    } catch {
      return null;
    }
  },
  write<T>(key: string, data: T): void {
    try {
      if (typeof window === "undefined") return;
      window.localStorage.setItem(
        `home_${HOME_CACHE_VERSION}::${key}`,
        JSON.stringify({ ts: Date.now(), data }),
      );
    } catch {
      // Quota exceeded / private mode — silently ignore, fetch path still runs.
    }
  },
};

/**
 * Curated, verified Unsplash photo IDs per B2B category keyword. Each entry
 * was confirmed to load (HTTP 200, valid JPEG) via direct CDN fetch. Keys are
 * regex patterns matched against the category name; first match wins.
 *
 * Categories that match nothing fall through to LoremFlickr in
 * getCategoryFallbackImageUrl() — that path still gives a relevant photo.
 */
const CATEGORY_UNSPLASH_RULES: Array<[RegExp, string]> = [
  [/agricultur|farm|crop/i, "1500382017468-9049fed747ef"],
  [/food|beverage|drink/i, "1504674900247-0877df9cc836"],
  [/grain|wheat|rice|cereal/i, "1574323347407-f5e1ad6d020b"],
  [/tile|ceramic/i, "1620626011761-996317b8d101"],
  [/textil|fabric|apparel|cloth|garment/i, "1606107557195-0e29a4b5b4aa"],
  [/electron/i, "1518770660439-4636190af475"],
  [/machin|equipment/i, "1556228720-195a672e8a03"],
  [/auto|vehicle|car|truck/i, "1492144534655-ae79c964c9d7"],
  [/chemic/i, "1532634922-8fe0b757fb13"],
  [/construct|building/i, "1541888946425-d81bb19240f5"],
  [/furnitur|interior|home decor/i, "1555041469-a586c61ea9bc"],
  [/beauty|cosmet|skincare|fragrance|perfume/i, "1596462502278-27bfdc403348"],
  [/jewel/i, "1605100804763-247f67b3557e"],
  [/medic|health|pharma/i, "1505751172876-fa1923c5c528"],
  [/packag/i, "1607082348824-0a96f2a4b9da"],
  [/metal|steel|iron|aluminum/i, "1568667256549-094345857637"],
  [/toy/i, "1558060370-d644479cb6f7"],
  [/sport|fitness|gym/i, "1517649763962-0c623066013b"],
  [/office|stationer/i, "1497032628192-86f99bcd76bc"],
];

/**
 * LoremFlickr fallback used when no curated Unsplash URL matches. Tag-based
 * Creative Commons Flickr lookup with a deterministic `lock` seed derived
 * from the category name so each tile gets a stable on-topic photo.
 */
function getLoremFlickrFallbackUrl(categoryName: string): string {
  const lower = (categoryName || "").toLowerCase().trim();
  const loremFlickrTags: Array<[RegExp, string]> = [
    [/plastic|polymer/, "plastic,industry"],
    [/leather/, "leather"],
    [/paper|print/, "paper,print"],
    [/light|lamp|led/, "lighting"],
    [/tool|hardware/, "tools,industrial"],
    [/security|safety/, "safety,security"],
    [/rubber/, "rubber,industry"],
  ];
  let tag = "products,business";
  for (const [rx, t] of loremFlickrTags) {
    if (rx.test(lower)) {
      tag = t;
      break;
    }
  }
  let hash = 0;
  for (let i = 0; i < categoryName.length; i++) {
    hash = (hash << 5) - hash + categoryName.charCodeAt(i);
    hash |= 0;
  }
  const lock = Math.abs(hash) % 100000;
  return `https://loremflickr.com/300/300/${encodeURIComponent(tag)}?lock=${lock}`;
}

/**
 * Image URL for Product Categories carousel tiles.
 * Prefers our hosted second-level category photos (Spaces `categories/`),
 * then catalog cover/icon, then curated Unsplash / LoremFlickr fallbacks.
 */
function getHomeProductCategoryImageUrl(
  category: { id: string; name: string },
  catalogImageById?: Map<string, string>,
): string {
  const curated = getHomeProductCategoryDisplayImageUrl(category.name);
  if (curated) return curated;
  const hosted = getCategorySecondaryImageUrl(category.name, category.id);
  if (hosted) return hosted;
  const catalog = catalogImageById?.get(String(category.id))?.trim();
  if (catalog) return catalog;
  return getCategoryFallbackImageUrl(category.name);
}

/**
 * Public image URL for a Shop By Category tile when no hosted/catalog image exists.
 * Prefers a curated, verified Unsplash photo when the category name matches a
 * known keyword; otherwise falls back to LoremFlickr by tag.
 */
function getCategoryFallbackImageUrl(categoryName: string): string {
  for (const [rx, photoId] of CATEGORY_UNSPLASH_RULES) {
    if (rx.test(categoryName || "")) {
      return `https://images.unsplash.com/photo-${photoId}?w=300&h=300&fit=crop&q=80&auto=format`;
    }
  }
  return getLoremFlickrFallbackUrl(categoryName);
}

function formatIndustryRowDisplayTitle(rowKey: string): string {
  return rowKey
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function normalizeIndustrySlugForMatch(raw: string): string {
  try {
    return decodeURIComponent(raw).trim().toLowerCase();
  } catch {
    return raw.trim().toLowerCase();
  }
}

function findCategoryForIndustrySlug(
  nodes: Category[],
  slugCandidates: string[],
): Category | null {
  const wants = new Set(slugCandidates.map(normalizeIndustrySlugForMatch));
  const walk = (list: Category[]): Category | null => {
    for (const node of list) {
      const rawSlug = String((node as { slug?: string }).slug || "").trim();
      if (rawSlug && wants.has(normalizeIndustrySlugForMatch(rawSlug))) {
        return node;
      }
      const nameAsSlug = normalizeIndustrySlugForMatch(
        String(node.name || "").replace(/\s+/g, "_"),
      );
      if (wants.has(nameAsSlug)) {
        return node;
      }
      if (Array.isArray(node.children) && node.children.length > 0) {
        const found = walk(node.children as Category[]);
        if (found) return found;
      }
    }
    return null;
  };
  return walk(nodes || []);
}

function extractProductsFromProductsApiResponse(res: unknown): any[] {
  if (!res || typeof res !== "object") return [];
  const top = res as Record<string, unknown>;
  const dataWrap = (top.data as Record<string, unknown> | undefined) ?? top;
  const nested = dataWrap.data as Record<string, unknown> | undefined;
  const list = dataWrap.products ?? nested?.products;
  return Array.isArray(list) ? list : [];
}

/** Best-effort membership label from common API shapes (public product listings). */
function extractProductMembershipPlanLabel(product: unknown): string {
  if (!product || typeof product !== "object") return "";
  const p = product as Record<string, any>;
  const company = p.company && typeof p.company === "object" ? p.company : null;
  const seller = p.seller && typeof p.seller === "object" ? p.seller : null;
  const assigned =
    p.assignedSeller && typeof p.assignedSeller === "object" ? p.assignedSeller : null;
  const vendor = p.vendor && typeof p.vendor === "object" ? p.vendor : null;
  const subPlan =
    company?.subscription && typeof company.subscription === "object"
      ? company.subscription.plan
      : null;
  const planName =
    (typeof subPlan === "object" && subPlan?.name) ||
    seller?.membershipPlanName ||
    seller?.membershipPlan?.name ||
    assigned?.membershipPlanName ||
    vendor?.membershipPlanName ||
    company?.membershipPlanName ||
    p.membershipPlanName ||
    p.membershipPlan ||
    "";
  return String(planName || "").trim();
}

/** Higher = more prominent plan (used for homepage ordering). */
function membershipPlanRankFromLabel(label: string): number {
  const n = label.toLowerCase();
  if (!n) return 0;
  if (n.includes("platinum")) return 5;
  if (n.includes("gold")) return 4;
  if (n.includes("silver")) return 3;
  if (n.includes("free")) return 1;
  if (n.includes("premium") || n.includes("enterprise")) return 4;
  return 0;
}

function extractProductSellerVerified(product: unknown): boolean {
  if (!product || typeof product !== "object") return false;
  const p = product as Record<string, any>;
  if (Boolean(p.isVerified)) return true;
  const company = p.company && typeof p.company === "object" ? p.company : null;
  const seller = p.seller && typeof p.seller === "object" ? p.seller : null;
  const assigned =
    p.assignedSeller && typeof p.assignedSeller === "object" ? p.assignedSeller : null;
  if (Boolean(seller?.isVerified ?? company?.isVerified ?? assigned?.isVerified))
    return true;
  const status = String(
    seller?.sellerStatus ?? assigned?.sellerStatus ?? company?.sellerStatus ?? "",
  ).toLowerCase();
  if (status === "approved" || status === "verified") return true;
  const v = String(p.verificationStatus ?? company?.verificationStatus ?? "").toLowerCase();
  if (v === "verified") return true;
  return false;
}

/**
 * Homepage / category rails: show higher membership tiers first, then verified sellers.
 * Stable for equal keys (preserves API order among ties).
 */
function sortProductsByMembershipPlanThenVerification<T>(products: T[]): T[] {
  if (!Array.isArray(products) || products.length <= 1) return [...products];
  return [...products].sort((a, b) => {
    const ra = membershipPlanRankFromLabel(extractProductMembershipPlanLabel(a));
    const rb = membershipPlanRankFromLabel(extractProductMembershipPlanLabel(b));
    if (rb !== ra) return rb - ra;
    const va = extractProductSellerVerified(a) ? 1 : 0;
    const vb = extractProductSellerVerified(b) ? 1 : 0;
    if (vb !== va) return vb - va;
    return 0;
  });
}

function buildInitialHomeIndustryShowcaseRows(): HomeIndustryShowcaseRow[] {
  return HOME_INDUSTRY_SLUG_ROWS.map((def) => ({
    rowKey: def.rowKey,
    title: formatIndustryRowDisplayTitle(def.rowKey),
    navigateCategoryId: null,
    products: [],
  }));
}

function withAlpha(color: string, alpha = 0.82): string {
  const value = (color || "").trim();
  const hex6 = /^#([0-9a-fA-F]{6})$/;
  const match = value.match(hex6);
  if (!match) return value || "rgba(223, 246, 207, 0.82)";
  const hex = match[1];
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function parseColorToRgb(
  color: string,
): { r: number; g: number; b: number } | null {
  const value = (color || "").trim();
  const hex6 = /^#([0-9a-fA-F]{6})$/;
  const hex3 = /^#([0-9a-fA-F]{3})$/;
  const rgb = /^rgba?\(\s*(\d{1,3})\s*[, ]\s*(\d{1,3})\s*[, ]\s*(\d{1,3})/i;

  const m6 = value.match(hex6);
  if (m6) {
    const h = m6[1];
    return {
      r: parseInt(h.slice(0, 2), 16),
      g: parseInt(h.slice(2, 4), 16),
      b: parseInt(h.slice(4, 6), 16),
    };
  }

  const m3 = value.match(hex3);
  if (m3) {
    const h = m3[1];
    return {
      r: parseInt(`${h[0]}${h[0]}`, 16),
      g: parseInt(`${h[1]}${h[1]}`, 16),
      b: parseInt(`${h[2]}${h[2]}`, 16),
    };
  }

  const mrgb = value.match(rgb);
  if (mrgb) {
    return {
      r: Math.min(255, parseInt(mrgb[1], 10)),
      g: Math.min(255, parseInt(mrgb[2], 10)),
      b: Math.min(255, parseInt(mrgb[3], 10)),
    };
  }

  return null;
}

function isDarkPanelColor(color: string): boolean {
  const parsed = parseColorToRgb(color);
  if (!parsed) return false;
  const { r, g, b } = parsed;
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  return luminance < 150;
}

function parseManagedSourceConfig(
  raw: unknown,
): ManagedHomepageSection["sourceConfig"] {
  if (!raw) return null;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object"
        ? (parsed as ManagedHomepageSection["sourceConfig"])
        : null;
    } catch {
      return null;
    }
  }
  return typeof raw === "object"
    ? (raw as ManagedHomepageSection["sourceConfig"])
    : null;
}

function parseArrayProducts(raw: unknown, prefix: string): ProductCardItem[] {
  if (!Array.isArray(raw)) return [];
  const sorted = sortProductsByMembershipPlanThenVerification(raw);
  return sorted.map((product: any, idx: number) =>
    mapHomepageProductToCardItem(product, prefix, idx),
  );
}

function mapHomepageProductToCardItem(
  product: any,
  fallbackPrefix: string,
  index: number,
): ProductCardItem {
  const priceValue = Number(product?.price || 0);
  const discountValue = Number(product?.discount || 0);
  const finalPrice =
    discountValue > 0 ? priceValue * (1 - discountValue / 100) : priceValue;
  const imageFromArray = product?.images?.[0];
  const imageUrl =
    product?.thumbnailImage ||
    product?.thumbnail?.url ||
    product?.image ||
    product?.imageUrl ||
    (typeof imageFromArray === "string"
      ? imageFromArray
      : imageFromArray?.url ||
        imageFromArray?.imageUrl ||
        imageFromArray?.src) ||
    "";
  const categoryName =
    product?.categoryRelation?.name ||
    (typeof product?.category === "string"
      ? product.category
      : product?.category?.name) ||
    product?.categoryName ||
    product?.subcategory ||
    "Uncategorized";
  const categoryId =
    product?.categoryRelation?.id ||
    product?.categoryId ||
    (typeof product?.category === "object" ? product?.category?.id : "") ||
    (categoryName
      ? String(categoryName).toLowerCase().replace(/\s+/g, "-")
      : `${fallbackPrefix}-category-${index + 1}`);
  const unitLabel = getProductUnitLabel(product);
  return {
    id: product?.id || `${fallbackPrefix}-product-${index + 1}`,
    name: product?.name || "Product",
    categoryId,
    category: categoryName,
    price: formatProductUsdPrice(finalPrice),
    oldPrice: formatProductUsdPrice(priceValue),
    unitLabel: unitLabel || undefined,
    imageUrl,
    brand: product?.brand || product?.brandName || "",
  };
}

function HomeHeroSideCtaStack({
  onJoinSupplier,
  onPostBuyingRequest,
}: {
  onJoinSupplier: () => void;
  onPostBuyingRequest: () => void;
}) {
  const shouldReduceMotion = useReducedMotion();
  return (
    <div className="grid h-full gap-4 lg:grid-rows-2">
      <motion.article
        className="relative flex h-full min-h-[180px] flex-col overflow-hidden rounded-[18px] bg-[#FDF2F4] p-5 sm:min-h-[210px] sm:p-6"
        initial={shouldReduceMotion ? false : { opacity: 0 }}
        animate={shouldReduceMotion ? undefined : { opacity: 1 }}
        transition={{ duration: 0.4, ease: EASE_OUT }}
        whileTap={shouldReduceMotion ? undefined : { scale: 0.99 }}
      >
        <div
          className="pointer-events-none absolute -right-8 -top-10 h-36 w-36 text-[#FBCFE8]"
          aria-hidden
        >
          <div className="absolute inset-0 rounded-full border-[14px] border-current opacity-50" />
          <div className="absolute inset-5 rounded-full border-[11px] border-current opacity-40" />
          <div className="absolute inset-10 rounded-full border-[9px] border-current opacity-35" />
        </div>
        <div className="relative z-[1] flex min-h-0 flex-1 flex-col text-left">
          <p className="text-[14px] font-semibold leading-snug text-[#5C6B7A]">
            List your Company
          </p>
          <p className="mt-1 text-[22px] font-bold leading-tight text-[#DC2626] sm:text-[24px]">
            For Free
          </p>
          <div className="mt-auto w-full pt-5">
            <button
              type="button"
              onClick={onJoinSupplier}
              className="home-shine-cta w-full rounded-full bg-[#DC2626] px-4 py-3 text-center text-[13px] font-bold text-white shadow-sm transition-colors hover:bg-[#B91C1C]"
            >
              Join as Supplier
            </button>
          </div>
        </div>
      </motion.article>

      <motion.article
        className="relative flex h-full min-h-[180px] flex-col overflow-hidden rounded-[18px] bg-[#EEF1F5] p-5 sm:min-h-[210px] sm:p-6"
        initial={shouldReduceMotion ? false : { opacity: 0 }}
        animate={shouldReduceMotion ? undefined : { opacity: 1 }}
        transition={{
          duration: 0.4,
          delay: shouldReduceMotion ? 0 : 0.04,
          ease: EASE_OUT,
        }}
        whileTap={shouldReduceMotion ? undefined : { scale: 0.99 }}
      >
        <div className="relative z-[1] flex min-h-0 flex-1 flex-col text-left">
          <p className="text-[14px] font-semibold leading-snug text-[#5C6B7A]">
            Looking to
          </p>
          <p className="mt-1 text-[22px] font-bold leading-tight text-[#111827] sm:text-[24px]">
            Source Products?
          </p>
          <div className="mt-auto w-full pt-5">
            <button
              type="button"
              onClick={onPostBuyingRequest}
              className="home-shine-cta w-full rounded-full bg-[#1F2937] px-4 py-3 text-center text-[13px] font-bold text-white shadow-sm transition-colors hover:bg-[#111827]"
            >
              Signup
            </button>
          </div>
        </div>
      </motion.article>
    </div>
  );
}

function BeautyFeatureCard({
  title,
  subtitle,
  cta,
  accentColor,
  tall = false,
  imageUrl,
  onCtaClick,
}: {
  title: string;
  subtitle: string;
  cta: string;
  accentColor: string;
  tall?: boolean;
  imageUrl?: string;
  onCtaClick?: () => void;
}) {
  const darkPanel = isDarkPanelColor(accentColor);
  return (
    <article
      className={`group relative overflow-hidden rounded-[16px] ${tall ? "h-[300px]" : "h-[278px]"}`}
      style={{
        backgroundImage: `url(${imageUrl || bannerImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/5 to-black/30" />
      {!imageUrl ? (
        <div className="flex h-full items-center justify-center px-4">
          <div className="h-[130px] w-[130px] rounded-[28px] bg-[#dfe4ec]" />
        </div>
      ) : null}
      {!HIDE_BANNER_TEXT_CONTENT ? (
        <div
          className="absolute inset-x-4 bottom-4 rounded-[10px] border border-white/40 px-4 py-3 shadow-[0px_10px_24px_rgba(15,52,96,0.14)] backdrop-blur-[4px]"
          style={{ backgroundColor: withAlpha(accentColor) }}
        >
          <span
            className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${
              darkPanel
                ? "bg-white/90 text-[#1f2937]"
                : "bg-[#ffe466] text-[#2b313d]"
            }`}
          >
            Enjoy 20% savings
          </span>
          <h3
            className={`mt-1 line-clamp-2 text-[18px] font-bold leading-[22px] ${darkPanel ? "text-white" : "text-[#131c26]"}`}
          >
            {title}
          </h3>
          <p
            className={`mt-1 line-clamp-2 text-[13px] leading-[16px] ${darkPanel ? "text-white/90" : "text-[#243242]"}`}
          >
            {subtitle}
          </p>
          <button
            type="button"
            onClick={onCtaClick}
            className={`mt-2 inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[11px] font-semibold ${
              darkPanel ? "bg-white text-[#F97316]" : "bg-[#F97316] text-white"
            }`}
          >
            {cta}
            <span
              className={`inline-flex h-4 w-4 items-center justify-center rounded-full ${
                darkPanel
                  ? "bg-[#F97316] text-white"
                  : "bg-white text-[#F97316]"
              }`}
            >
              <MoveUpRightIcon className="h-4 w-4 p-0.5" />
            </span>
          </button>
        </div>
      ) : null}
    </article>
  );
}

type CommonProductCardProps = {
  id: string;
  name: string;
  imageUrl?: string;
  onAction: (id: string) => void;
  cardClassName: string;
  imageWrapperClassName: string;
  imageClassName: string;
  fallbackNode: ReactNode;
  topNode?: ReactNode;
  metaNode: ReactNode;
  footerNode?: ReactNode;
  actionLabel: string;
  actionClassName: string;
  actionIcon?: ReactNode;
  saveButtonClassName?: string;
  contentSpacingClassName?: string;
  isFavorited?: boolean;
  isFavoriteLoading?: boolean;
  onToggleFavorite?: (id: string, isFavorited: boolean) => void;
  actionWrapperClassName?: string;
  hoverTitle?: string;
};

function CommonProductCard({
  id,
  name,
  imageUrl,
  onAction,
  cardClassName,
  imageWrapperClassName,
  imageClassName,
  fallbackNode,
  topNode,
  metaNode,
  footerNode,
  actionLabel,
  actionClassName,
  actionIcon,
  saveButtonClassName = "inline-flex h-8 w-8 items-center justify-center rounded-full border border-[#d9e1ea] text-[#637381]",
  contentSpacingClassName = "mt-2",
  isFavorited = false,
  isFavoriteLoading = false,
  onToggleFavorite,
  actionWrapperClassName = "mt-2 flex items-center gap-2",
  hoverTitle,
}: CommonProductCardProps) {
  const handleCardActivate = () => {
    onAction(id);
  };

  return (
    <article
      className={`cursor-pointer ${cardClassName}`}
      title={hoverTitle}
      tabIndex={0}
      role="group"
      aria-label={`${name}. ${actionLabel}.`}
      onClick={handleCardActivate}
      onKeyDown={(e) => {
        if (e.key !== "Enter" && e.key !== " ") return;
        if ((e.target as HTMLElement).closest("button")) return;
        e.preventDefault();
        handleCardActivate();
      }}
    >
      <div className={imageWrapperClassName}>
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={name}
            className={imageClassName}
            loading="lazy"
          />
        ) : (
          fallbackNode
        )}
      </div>
      <div className={contentSpacingClassName}>
        {topNode}
        {metaNode}
        {footerNode}
        <div className={actionWrapperClassName}>
          {onToggleFavorite ? (
            <button
              type="button"
              className={saveButtonClassName}
              aria-label={
                isFavorited ? `Remove ${name} from favorites` : `Save ${name}`
              }
              onClick={(ev) => {
                ev.stopPropagation();
                onToggleFavorite(id, isFavorited);
              }}
              disabled={isFavoriteLoading}
            >
              <span className={isFavorited ? "text-[#ff4d6a]" : ""}>
                {isFavorited ? "♥" : "♡"}
              </span>
            </button>
          ) : null}
          <button
            type="button"
            onClick={(ev) => {
              ev.stopPropagation();
              onAction(id);
            }}
            className={actionClassName}
          >
            {actionIcon}
            {actionLabel}
          </button>
        </div>
      </div>
    </article>
  );
}

function ProductCard({
  id,
  name,
  category,
  price,
  unitLabel,
  imageUrl,
  onViewProduct,
  isFavorited,
  isFavoriteLoading,
  onToggleFavorite,
}: {
  id: string;
  name: string;
  category: string;
  price: string;
  unitLabel?: string;
  oldPrice: string;
  imageUrl?: string;
  onViewProduct: (id: string) => void;
} & FavoriteCardProps) {
  const safePrice = getSafePrice(price);
  return (
    <article
      role="group"
      tabIndex={0}
      aria-label={`${name}. View product.`}
      className="group min-w-0 cursor-pointer rounded-xl border border-gray-200 bg-white p-2.5 transition-shadow hover:shadow-md outline-none focus-visible:ring-2 focus-visible:ring-[#F97316] focus-visible:ring-offset-2"
      onClick={() => onViewProduct(id)}
      onKeyDown={(e) => {
        if (e.key !== "Enter" && e.key !== " ") return;
        if ((e.target as HTMLElement).closest("button, a")) return;
        e.preventDefault();
        onViewProduct(id);
      }}
    >
      <div className="relative aspect-[4/3] overflow-hidden rounded-lg border border-gray-100 bg-gray-50">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={name}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-gray-400 text-sm">
            No Image Preview
          </div>
        )}
      </div>

      <div className="mt-3">
        <h3 className="line-clamp-2 min-h-[2.5rem] text-sm font-semibold text-gray-900 break-words">
          {name}
        </h3>

        <div className="mt-2 flex min-w-0 items-center gap-1 text-xs text-gray-500">
          <span className="truncate block">{category || "Supplier"}</span>
          <span className="inline-flex items-center text-green-700">
            <CheckCircle2 className="h-3.5 w-3.5" />
          </span>
        </div>

        <div className="mt-2">
          <span className="text-base font-bold tabular-nums text-[#F97316]">
            {price.startsWith("$") ? price : `$${safePrice.toFixed(2)}`}
          </span>
          <ProductCardPerUnitLine unitLabel={unitLabel} />
        </div>

        <div className="mt-3 flex items-center gap-1.5">
          {onToggleFavorite && (
            <button
              type="button"
              disabled={isFavoriteLoading}
              onClick={(ev) => {
                ev.stopPropagation();
                onToggleFavorite(id, Boolean(isFavorited));
              }}
              className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-gray-200 text-gray-500 transition-colors hover:border-[#F97316] hover:text-[#F97316] disabled:opacity-50"
              aria-label={isFavorited ? `Remove ${name} from favorites` : `Save ${name}`}
            >
              <Heart
                className={`h-4 w-4 ${
                  isFavorited ? "fill-[#F97316] text-[#F97316]" : ""
                }`}
              />
            </button>
          )}

          <Button
            size="sm"
            className="h-7 min-w-0 flex-1 rounded-full bg-[#F97316] px-2 text-white hover:bg-[#ea6820] whitespace-nowrap"
            onClick={(ev) => {
              ev.stopPropagation();
              onViewProduct(id);
            }}
          >
            <Eye className="mr-1 h-3.5 w-3.5" />
            View Product
          </Button>
        </div>
      </div>
    </article>
  );
}

function LaunchProductCard({
  id,
  name,
  price,
  unitLabel,
  imageUrl,
  onViewProduct,
  isFavorited,
  isFavoriteLoading,
  onToggleFavorite,
}: {
  id: string;
  name: string;
  price: string;
  unitLabel?: string;
  oldPrice: string;
  imageUrl?: string;
  onViewProduct: (id: string) => void;
} & FavoriteCardProps) {
  const safePrice = getSafePrice(price);
  return (
    <article
      role="group"
      tabIndex={0}
      aria-label={`${name}. View product.`}
      className="group min-w-0 cursor-pointer rounded-xl border border-gray-200 bg-white p-2.5 transition-shadow hover:shadow-md outline-none focus-visible:ring-2 focus-visible:ring-[#F97316] focus-visible:ring-offset-2"
      onClick={() => onViewProduct(id)}
      onKeyDown={(e) => {
        if (e.key !== "Enter" && e.key !== " ") return;
        if ((e.target as HTMLElement).closest("button, a")) return;
        e.preventDefault();
        onViewProduct(id);
      }}
    >
      <div className="relative aspect-[4/3] overflow-hidden rounded-lg border border-gray-100 bg-gray-50">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={name}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-gray-400 text-sm">
            No Image Preview
          </div>
        )}
      </div>

      <div className="mt-3">
        <h3 className="line-clamp-2 min-h-[2.5rem] text-sm font-semibold text-gray-900 break-words">
          {name}
        </h3>

        <div className="mt-2 flex min-w-0 items-center gap-1 text-xs text-gray-500">
          <span className="truncate block">Supplier</span>
          <span className="inline-flex items-center text-green-700">
            <CheckCircle2 className="h-3.5 w-3.5" />
          </span>
        </div>

        <div className="mt-2">
          <span className="text-base font-bold tabular-nums text-[#F97316]">
            {price.startsWith("$") ? price : `$${safePrice.toFixed(2)}`}
          </span>
          <ProductCardPerUnitLine unitLabel={unitLabel} />
        </div>

        <div className="mt-3 flex items-center gap-1.5">
          {onToggleFavorite && (
            <button
              type="button"
              disabled={isFavoriteLoading}
              onClick={(ev) => {
                ev.stopPropagation();
                onToggleFavorite(id, Boolean(isFavorited));
              }}
              className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-gray-200 text-gray-500 transition-colors hover:border-[#F97316] hover:text-[#F97316] disabled:opacity-50"
              aria-label={isFavorited ? `Remove ${name} from favorites` : `Save ${name}`}
            >
              <Heart
                className={`h-4 w-4 ${
                  isFavorited ? "fill-[#F97316] text-[#F97316]" : ""
                }`}
              />
            </button>
          )}

          <Button
            size="sm"
            className="h-7 min-w-0 flex-1 rounded-full bg-[#F97316] px-2 text-white hover:bg-[#ea6820] whitespace-nowrap"
            onClick={(ev) => {
              ev.stopPropagation();
              onViewProduct(id);
            }}
          >
            <Eye className="mr-1 h-3.5 w-3.5" />
            View Product
          </Button>
        </div>
      </div>
    </article>
  );
}

function getHomeCarouselTotalPages(productCount: number): number {
  return Math.max(1, Math.ceil(productCount / BEAUTY_PAGE_SIZE));
}

function HomeCarouselNavButtons({
  page,
  totalPages,
  onPrev,
  onNext,
  size = "md",
}: {
  page: number;
  totalPages: number;
  onPrev: () => void;
  onNext: () => void;
  size?: "md" | "sm";
}) {
  if (totalPages <= 1) return null;
  const btnClass =
    size === "sm" ? HOME_SECTION_NAV_BTN_SM : HOME_SECTION_NAV_BTN_MD;
  return (
    <>
      <button
        type="button"
        onClick={onPrev}
        disabled={page <= 0}
        className={btnClass}
        aria-label="Previous products"
      >
        <ChevronLeft className="h-4 w-4" strokeWidth={2.25} />
      </button>
      <button
        type="button"
        onClick={onNext}
        disabled={page >= totalPages - 1}
        className={btnClass}
        aria-label="Next products"
      >
        <ChevronRight className="h-4 w-4" strokeWidth={2.25} />
      </button>
    </>
  );
}

function HomePaginatedProductGrid({
  products,
  carouselKey,
  page,
  canShowFavorites,
  favoriteIds,
  favoriteLoadingIds,
  onToggleFavorite,
  onViewProduct,
  shouldReduceMotion,
}: {
  products: ProductCardItem[];
  carouselKey: string;
  page: number;
  canShowFavorites: boolean;
  favoriteIds: Set<string>;
  favoriteLoadingIds: Set<string>;
  onToggleFavorite?: (id: string, isFavorited: boolean) => void;
  onViewProduct: (id: string) => void;
  shouldReduceMotion: boolean | null;
}) {
  if (products.length === 0) return null;

  if (products.length === 1) {
    const item = products[0];
    return (
      <motion.div
        className="relative z-[2] mt-2 grid grid-cols-1 items-stretch gap-3 pb-0 sm:max-w-md"
        initial={shouldReduceMotion ? false : "hidden"}
        animate={shouldReduceMotion ? undefined : "show"}
        variants={shouldReduceMotion ? undefined : PRODUCT_STAGGER_CONTAINER}
      >
        <motion.div
          className="h-full"
          variants={shouldReduceMotion ? undefined : PRODUCT_STAGGER_ITEM}
        >
          <LaunchProductCard
            id={item.id}
            name={item.name}
            price={item.price}
            unitLabel={item.unitLabel}
            oldPrice={item.oldPrice}
            imageUrl={item.imageUrl}
            onViewProduct={onViewProduct}
            isFavorited={
              canShowFavorites ? favoriteIds.has(String(item.id)) : false
            }
            isFavoriteLoading={
              canShowFavorites ? favoriteLoadingIds.has(String(item.id)) : false
            }
            onToggleFavorite={onToggleFavorite}
          />
        </motion.div>
      </motion.div>
    );
  }

  const totalPages = getHomeCarouselTotalPages(products.length);
  const safePage = Math.min(Math.max(0, page), totalPages - 1);
  const pageStart = safePage * BEAUTY_PAGE_SIZE;
  const pageProducts = products.slice(pageStart, pageStart + BEAUTY_PAGE_SIZE);

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={`${carouselKey}-page-${safePage}`}
        className={HOME_CAROUSEL_PRODUCT_GRID_CLASS}
        initial={shouldReduceMotion ? false : { opacity: 0 }}
        animate={shouldReduceMotion ? undefined : { opacity: 1 }}
        exit={shouldReduceMotion ? undefined : { opacity: 0 }}
        transition={{ duration: 0.35, ease: EASE_OUT }}
      >
        {pageProducts.map((item) => (
          <motion.div className="h-full" key={`${carouselKey}-${item.id}`}>
            <LaunchProductCard
              id={item.id}
              name={item.name}
              price={item.price}
              unitLabel={item.unitLabel}
              oldPrice={item.oldPrice}
              imageUrl={item.imageUrl}
              onViewProduct={onViewProduct}
              isFavorited={
                canShowFavorites ? favoriteIds.has(String(item.id)) : false
              }
              isFavoriteLoading={
                canShowFavorites
                  ? favoriteLoadingIds.has(String(item.id))
                  : false
              }
              onToggleFavorite={onToggleFavorite}
            />
          </motion.div>
        ))}
      </motion.div>
    </AnimatePresence>
  );
}

function TopOfferCard({
  id,
  name,
  category,
  price,
  unitLabel,
  oldPrice,
  imageUrl,
  onViewProduct,
  isFavorited,
  isFavoriteLoading,
  onToggleFavorite,
}: {
  id: string;
  name: string;
  category: string;
  price: string;
  unitLabel?: string;
  oldPrice: string;
  imageUrl?: string;
  onViewProduct: (id: string) => void;
} & FavoriteCardProps) {
  return (
    <CommonProductCard
      id={id}
      name={name}
      imageUrl={imageUrl}
      onAction={onViewProduct}
      cardClassName="flex h-full min-h-[268px] flex-col rounded-[14px] bg-white p-2.5 shadow-[0px_2px_8px_rgba(0,0,0,0.05)]"
      imageWrapperClassName="flex h-[136px] shrink-0 items-center justify-center rounded-[10px] bg-[#f7fbff]"
      imageClassName="h-full w-full rounded-[10px] object-contain"
      fallbackNode={
        <>
          <div className="h-[64px] w-[64px] rounded-[16px] bg-[#dfe4ec]" />
          <p className="sr-only">No Image Preview</p>
        </>
      }
      contentSpacingClassName="mt-2 flex min-h-0 flex-1 flex-col"
      metaNode={
        <div className="flex min-h-[4.25rem] flex-1 flex-col">
          <p className="line-clamp-1 text-[10px] text-[#7a8796]">{category}</p>
          <h3 className="mt-1 line-clamp-2 text-[13px] font-semibold leading-[17px] text-[#212529]">
            {name}
          </h3>
          <div className="mt-auto pt-1.5 text-[12px]">
            <span className="font-bold tabular-nums text-[#111827]">{price}</span>
            <ProductCardPerUnitLine unitLabel={unitLabel} />
          </div>
        </div>
      }
      saveButtonClassName="inline-flex h-7 w-7 items-center justify-center rounded-full border border-[#d9e1ea] text-[#637381]"
      actionLabel="View Product"
      actionClassName="inline-flex h-7 flex-1 items-center justify-center gap-1 rounded-full bg-[#F97316] px-2 text-[10px] font-semibold text-white"
      actionWrapperClassName="mt-auto flex shrink-0 items-center gap-2 pt-2"
      isFavorited={isFavorited}
      isFavoriteLoading={isFavoriteLoading}
      onToggleFavorite={onToggleFavorite}
    />
  );
}

function isCatalogCategoryActive(category: Category): boolean {
  const raw = category as unknown as {
    isActive?: boolean;
    active?: boolean;
    status?: string;
  };
  if (typeof raw?.isActive === "boolean") return raw.isActive;
  if (typeof raw?.active === "boolean") return raw.active;
  if (typeof raw?.status === "string")
    return raw.status.toLowerCase() === "active";
  return true;
}

function filterActiveCategoryTree(nodes: Category[]): Category[] {
  return (nodes || []).reduce<Category[]>((acc, node) => {
    if (!isCatalogCategoryActive(node)) return acc;
    const children = Array.isArray(node.children)
      ? filterActiveCategoryTree(node.children as Category[])
      : [];
    acc.push({ ...node, children });
    return acc;
  }, []);
}

function extractCategoryProductCount(category: Category): number {
  const raw = category as Category & { productCount?: unknown };
  const count = raw.productCount;
  return typeof count === "number" && Number.isFinite(count) ? count : 0;
}

/** Second-level categories: direct children of each active top-level (industry) node. */
function collectSecondLevelCategories(topLevelTree: Category[]): Category[] {
  const result: Category[] = [];
  for (const root of topLevelTree || []) {
    if (!isCatalogCategoryActive(root)) continue;
    const children = Array.isArray(root.children) ? (root.children as Category[]) : [];
    for (const child of children) {
      if (isCatalogCategoryActive(child)) {
        result.push(child);
      }
    }
  }
  return result;
}

function buildHomeProductCategorySlides(topLevelTree: Category[]): {
  id: string;
  name: string;
}[] {
  const idByNameKey = new Map<string, string>();
  collectSecondLevelCategories(topLevelTree).forEach((category) => {
    const name = String(category.name || "").trim();
    if (!name) return;
    idByNameKey.set(normalizeCategorySecondaryImageKey(name), String(category.id));
  });

  return HOME_PRODUCT_CATEGORY_DISPLAY.map((row) => ({
    id:
      idByNameKey.get(normalizeCategorySecondaryImageKey(row.name)) ?? row.name,
    name: row.name,
  }));
}

function collectCategoryIds(nodes: Category[]): Set<string> {
  const ids = new Set<string>();
  const walk = (list: Category[]) => {
    list.forEach((n) => {
      ids.add(String(n.id));
      if (Array.isArray(n.children) && n.children.length > 0) {
        walk(n.children as Category[]);
      }
    });
  };
  walk(nodes);
  return ids;
}

/** Products without a catalog category use synthetic id/name "uncategorized" — not in the API tree. */
function isUncategorizedShopSlide(category: {
  id: string;
  name: string;
}): boolean {
  const name = String(category.name || "")
    .trim()
    .toLowerCase();
  if (name === "uncategorized") return true;
  const id = String(category.id || "")
    .trim()
    .toLowerCase();
  return id === "uncategorized";
}

function DealRowCard({
  id,
  name,
  price,
  unitLabel,
  oldPrice,
  imageUrl,
  onViewProduct,
}: {
  id: string;
  name: string;
  price: string;
  unitLabel?: string;
  oldPrice: string;
  imageUrl?: string;
  onViewProduct: (id: string) => void;
}) {
  const go = () => onViewProduct(id);

  return (
    <article
      className="grid cursor-pointer grid-cols-[56px_1fr_auto] items-center gap-3 rounded-[16px] bg-white p-3 shadow-[0px_4px_20px_rgba(15,52,96,0.06)]"
      tabIndex={0}
      role="group"
      aria-label={`${name}. View product.`}
      onClick={go}
      onKeyDown={(e) => {
        if (e.key !== "Enter" && e.key !== " ") return;
        if ((e.target as HTMLElement).closest("button")) return;
        e.preventDefault();
        go();
      }}
    >
      <div
        className="flex h-[56px] w-[56px] shrink-0 items-center justify-center rounded-[12px] bg-[#eef3f9]"
        aria-hidden
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt=""
            className="h-12 w-12 rounded-[8px] object-cover"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center px-1">
            <div className="h-8 w-8 rounded-[6px] border border-[#d7dee8] bg-white" />
            <p className="mt-0.5 text-center text-[7px] leading-tight text-[#919eab]">
              No Image
            </p>
          </div>
        )}
      </div>
      <div className="min-w-0">
        <h4 className="line-clamp-2 text-[12px] font-semibold leading-[1.35] text-[#212529]">
          {name}
        </h4>
        {/* <p className="mt-1 text-[11px] leading-none">
          <span className="text-[#919eab]"> (118)</span>
        </p> */}
        <div className="mt-1 text-[12px]">
          <span className="font-bold tabular-nums text-[#212529]">{price}</span>
          <ProductCardPerUnitLine unitLabel={unitLabel} />
        </div>
      </div>
      <button
        type="button"
        onClick={(ev) => {
          ev.stopPropagation();
          go();
        }}
        className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[#F97316] px-3 py-2 text-[11px] font-semibold text-white shadow-sm"
        aria-label={`View ${name}`}
      >
        View Product
      </button>
    </article>
  );
}

export default function BizcoverIndiaUpdatedHomePage() {
  const navigate = useNavigate();
  const shouldReduceMotion = useReducedMotion();
  const { isAuthenticated, userRole, userStatus } = useAuth();
  const [query, setQuery] = useState("");
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [categories, setCategories] = useState<Category[]>(
    () => homeCache.read<Category[]>("categories") ?? [],
  );
  const [catalogCategoriesLoaded, setCatalogCategoriesLoaded] = useState(false);
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set());
  const [favoriteLoadingIds, setFavoriteLoadingIds] = useState<Set<string>>(
    new Set(),
  );
  const [topPromos, setTopPromos] = useState<TopPromoItem[]>(
    () =>
      homeCache.read<TopPromoItem[]>("topPromos") ??
      [
        {
          id: "fallback-main-hero",
          title: "Connect Indian Exporters with Global Buyers",
          subtitle: "B2B Portal + Virtual Expo Platform",
          bg: "#EAF2FF",
          cta: "Explore Portal",
          imageUrl: HOME_HERO_BANNER_IMAGE_URL,
          ctaUrl: "/search",
        },
      ],
  );
  const [bottomFeatureTiles, setBottomFeatureTiles] = useState<
    BeautyFeatureItem[]
  >(
    () =>
      homeCache.read<BeautyFeatureItem[]>("bottomFeatureTiles") ??
      BEAUTY_FEATURE_TILES.map((tile) => ({
        id: tile.id,
        title: tile.title,
        subtitle: tile.subtitle,
        cta: tile.cta,
        accentColor: BOTTOM_BANNER_ACCENTS[0],
        imageUrl: "",
      })),
  );
  const [managedTopSections, setManagedTopSections] = useState<
    ManagedHomepageSection[]
  >(() => homeCache.read<ManagedHomepageSection[]>("managedTopSections") ?? []);
  const [managedBottomSections, setManagedBottomSections] = useState<
    ManagedHomepageSection[]
  >(
    () =>
      homeCache.read<ManagedHomepageSection[]>("managedBottomSections") ?? [],
  );
  const [managedSectionPages, setManagedSectionPages] = useState<
    Record<string, number>
  >({});
  const [managedGridSectionPages, setManagedGridSectionPages] = useState<
    Record<string, { sectionOne: number; sectionTwo: number }>
  >({});
  const [apiProducts, setApiProducts] = useState<ProductCardItem[]>(
    () =>
      homeCache.read<ProductCardItem[]>("apiProducts") ?? OUR_PRODUCTS_ITEMS,
  );
  const [bestSellingProducts, setBestSellingProducts] = useState<
    ProductCardItem[]
  >(() => homeCache.read<ProductCardItem[]>("bestSellingProducts") ?? []);
  const [newArrivalProducts, setNewArrivalProducts] = useState<
    ProductCardItem[]
  >(() => homeCache.read<ProductCardItem[]>("newArrivalProducts") ?? []);
  const [featuredProducts, setFeaturedProducts] = useState<ProductCardItem[]>(
    () => homeCache.read<ProductCardItem[]>("featuredProducts") ?? [],
  );
  const [industryShowcaseRows, setIndustryShowcaseRows] = useState<
    HomeIndustryShowcaseRow[]
  >(
    () =>
      homeCache.read<HomeIndustryShowcaseRow[]>("industryShowcaseRows") ??
      buildInitialHomeIndustryShowcaseRows(),
  );
  const [industryShowcaseLoading, setIndustryShowcaseLoading] = useState(false);
  const [activeTopPromoIndex, setActiveTopPromoIndex] = useState(0);
  const liveBrands = useMemo(() => {
    const unique = new Set(
      apiProducts
        .map((item) => item.brand?.trim())
        .filter((brand): brand is string => Boolean(brand)),
    );
    return Array.from(unique).slice(0, 12);
  }, [apiProducts]);

  const [categorySliderPage, setCategorySliderPage] = useState(0);
  const [beautyPage, setBeautyPage] = useState(0);
  const [featuredPage, setFeaturedPage] = useState(0);
  const [pauseNewArrivalsAuto, setPauseNewArrivalsAuto] = useState(false);
  const [pauseFeaturedAuto, setPauseFeaturedAuto] = useState(false);
  const [industrySectionPages, setIndustrySectionPages] = useState<
    Record<string, number>
  >({});
  const [pausedIndustryCarouselKey, setPausedIndustryCarouselKey] = useState<
    string | null
  >(null);
  const launchProducts =
    newArrivalProducts.length > 0 ? newArrivalProducts : apiProducts;
  const beautyTotalPages = getHomeCarouselTotalPages(launchProducts.length);
  const featuredTotalPages = getHomeCarouselTotalPages(featuredProducts.length);

  const bestSellingPreview = useMemo(
    () =>
      (bestSellingProducts.length > 0
        ? bestSellingProducts
        : apiProducts
      ).slice(0, 10),
    [bestSellingProducts, apiProducts],
  );
  const currentTopPromo = topPromos[activeTopPromoIndex] ||
    topPromos[0] || {
      id: "fallback-main-hero",
      title: "Connect Indian Exporters with Global Buyers",
      subtitle: "B2B Portal + Virtual Expo Platform",
      bg: "#EAF2FF",
      cta: "Explore Portal",
      imageUrl: HOME_HERO_BANNER_IMAGE_URL,
      ctaUrl: "/search",
    };
  const categoryImageById = useMemo(() => {
    const map = new Map<string, string>();
    const walk = (nodes: Category[]) => {
      nodes.forEach((node) => {
        const img = String(
          (node as any)?.coverImage || (node as any)?.icon || "",
        ).trim();
        if (img) map.set(String(node.id), img);
        if (Array.isArray(node.children) && node.children.length > 0) {
          walk(node.children as Category[]);
        }
      });
    };
    walk(categories);
    return map;
  }, [categories]);
  const categoryProductSlides = useMemo(
    () => buildHomeProductCategorySlides(categories),
    [categories],
  );
  const categorySliderTotalPages = Math.max(
    1,
    Math.ceil(categoryProductSlides.length / CATEGORY_SLIDER_PAGE_SIZE),
  );
  const categorySliderStart = categorySliderPage * CATEGORY_SLIDER_PAGE_SIZE;
  const visibleCategorySlides = categoryProductSlides.slice(
    categorySliderStart,
    categorySliderStart + CATEGORY_SLIDER_PAGE_SIZE,
  );
  const canShowFavorites =
    isAuthenticated && userStatus === "active" && userRole === "buyer";

  useEffect(() => {
    setCategorySliderPage((prev) =>
      Math.min(prev, categorySliderTotalPages - 1),
    );
  }, [categorySliderTotalPages]);

  useEffect(() => {
    setBeautyPage((prev) => Math.min(prev, beautyTotalPages - 1));
  }, [beautyTotalPages]);

  useEffect(() => {
    setFeaturedPage((prev) => Math.min(prev, featuredTotalPages - 1));
  }, [featuredTotalPages]);

  useEffect(() => {
    if (pauseNewArrivalsAuto || beautyTotalPages <= 1 || shouldReduceMotion) {
      return;
    }
    const timerId = window.setInterval(() => {
      setBeautyPage((prev) => (prev + 1 >= beautyTotalPages ? 0 : prev + 1));
    }, HOME_PRODUCT_CAROUSEL_AUTO_MS);
    return () => window.clearInterval(timerId);
  }, [pauseNewArrivalsAuto, beautyTotalPages, shouldReduceMotion]);

  useEffect(() => {
    if (pauseFeaturedAuto || featuredTotalPages <= 1 || shouldReduceMotion) {
      return;
    }
    const timerId = window.setInterval(() => {
      setFeaturedPage((prev) =>
        prev + 1 >= featuredTotalPages ? 0 : prev + 1,
      );
    }, HOME_PRODUCT_CAROUSEL_AUTO_MS);
    return () => window.clearInterval(timerId);
  }, [pauseFeaturedAuto, featuredTotalPages, shouldReduceMotion]);

  useEffect(() => {
    if (shouldReduceMotion) return;
    const timerId = window.setInterval(() => {
      setIndustrySectionPages((prev) => {
        const next = { ...prev };
        industryShowcaseRows.forEach((row) => {
          if (pausedIndustryCarouselKey === row.rowKey) return;
          const totalPages = getHomeCarouselTotalPages(row.products.length);
          if (totalPages <= 1) return;
          const current = prev[row.rowKey] ?? 0;
          next[row.rowKey] =
            current + 1 >= totalPages ? 0 : current + 1;
        });
        return next;
      });
    }, HOME_PRODUCT_CAROUSEL_AUTO_MS);
    return () => window.clearInterval(timerId);
  }, [industryShowcaseRows, pausedIndustryCarouselKey, shouldReduceMotion]);

  useEffect(() => {
    let active = true;

    const extractProducts = (responseLike: any): any[] => {
      if (!responseLike) return [];
      const responseData = responseLike?.data || responseLike;
      const list =
        responseData?.products || responseData?.data?.products || [];
      return Array.isArray(list) ? list : [];
    };

    const mapProducts = (list: any[], prefix: string): ProductCardItem[] =>
      sortProductsByMembershipPlanThenVerification(list)
        .slice(0, 30)
        .map((product: any, index: number) =>
          mapHomepageProductToCardItem(product, prefix, index),
        );

    const applyHomepageProducts = (
      result: PromiseSettledResult<unknown>,
      label: string,
      prefix: string,
      onSuccess: (mapped: ProductCardItem[]) => void,
      cacheKey:
        | "apiProducts"
        | "bestSellingProducts"
        | "newArrivalProducts"
        | "featuredProducts",
    ) => {
      if (result.status !== "fulfilled") {
        console.error(`Failed to fetch ${label} homepage products:`, result.reason);
        return;
      }
      const mapped = mapProducts(extractProducts(result.value), prefix);
      if (mapped.length > 0) {
        onSuccess(mapped);
        homeCache.write(cacheKey, mapped);
      }
    };

    // Fetch independently — a slow/timeout on featured must not block new arrivals.
    Promise.allSettled([
      productService.getFeaturedProducts(30),
      productService.getTopDeals(30),
      productService.getNewArrivals(30),
    ]).then((results) => {
      if (!active) return;
      applyHomepageProducts(
        results[0],
        "featured",
        "featured",
        (mapped) => {
          setFeaturedProducts(mapped);
          setApiProducts(mapped);
          homeCache.write("apiProducts", mapped);
        },
        "featuredProducts",
      );
      applyHomepageProducts(
        results[1],
        "top deals",
        "best-selling",
        setBestSellingProducts,
        "bestSellingProducts",
      );
      applyHomepageProducts(
        results[2],
        "new arrivals",
        "new-arrival",
        setNewArrivalProducts,
        "newArrivalProducts",
      );
    });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!canShowFavorites) {
      setFavoriteIds(new Set());
      return;
    }
    let active = true;
    favoritesService
      .getFavorites()
      .then((response: any) => {
        if (!active) return;
        const favorites = Array.isArray(response?.favorites)
          ? response.favorites
          : [];
        setFavoriteIds(
          new Set(favorites.map((favorite: any) => String(favorite.id))),
        );
      })
      .catch((err) => {
        console.error("Failed to fetch favorites:", err);
      });
    return () => {
      active = false;
    };
  }, [canShowFavorites]);

  useEffect(() => {
    let cancelled = false;
    setCatalogCategoriesLoaded(false);
    adminService
      .getCategories(true)
      .then((data: any) => {
        if (cancelled) return;
        const allCategories = Array.isArray(data) ? data : [];
        const topLevel = allCategories.filter((c: any) => !c.parentId);
        const filtered = filterActiveCategoryTree(topLevel as Category[]);
        setCategories(filtered);
        homeCache.write("categories", filtered);
      })
      .catch((err) => {
        console.error("Failed to fetch categories for mega menu:", err);
      })
      .finally(() => {
        if (!cancelled) setCatalogCategoriesLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!catalogCategoriesLoaded) return;
    let active = true;
    setIndustryShowcaseLoading(true);

    const rowsMeta = HOME_INDUSTRY_SLUG_ROWS.map((def) => {
      const matched = findCategoryForIndustrySlug(
        categories,
        def.slugCandidates,
      );
      const slugFallback = def.slugCandidates[0] || def.rowKey;
      return {
        rowKey: def.rowKey,
        title: matched?.name
          ? String(matched.name)
          : formatIndustryRowDisplayTitle(def.rowKey),
        navigateCategoryId: matched?.id ? String(matched.id) : null,
        slugFallback,
      };
    });

    Promise.all(
      rowsMeta.map(async (meta) => {
        const queryKey = meta.navigateCategoryId || meta.slugFallback;
        try {
          const res = await productService.getProductsByCategory(queryKey, {
            page: 1,
            limit: HOME_INDUSTRY_FETCH_LIMIT,
            sortBy: "createdAt",
            sortOrder: "desc",
            includeDescendants: true,
          });
          const list = extractProductsFromProductsApiResponse(res);
          const ranked = sortProductsByMembershipPlanThenVerification(list);
          let navigateCategoryId = meta.navigateCategoryId;
          if (!navigateCategoryId && list[0]?.categoryRelation?.id) {
            navigateCategoryId = String(list[0].categoryRelation.id);
          }
          const products = ranked
            .slice(0, HOME_INDUSTRY_PRODUCT_LIMIT)
            .map((p: any, idx: number) =>
              mapHomepageProductToCardItem(p, `industry-${meta.rowKey}`, idx),
            );
          return {
            rowKey: meta.rowKey,
            title: meta.title,
            navigateCategoryId,
            products,
          };
        } catch (err) {
          console.error(
            `Failed to fetch industry showcase (${meta.rowKey}):`,
            err,
          );
          return {
            rowKey: meta.rowKey,
            title: meta.title,
            navigateCategoryId: meta.navigateCategoryId,
            products: [] as ProductCardItem[],
          };
        }
      }),
    )
      .then((resolved) => {
        if (!active) return;
        setIndustryShowcaseRows(resolved);
        homeCache.write("industryShowcaseRows", resolved);
      })
      .finally(() => {
        if (active) setIndustryShowcaseLoading(false);
      });

    return () => {
      active = false;
    };
  }, [categories, catalogCategoriesLoaded]);

  useEffect(() => {
    setActiveTopPromoIndex((prev) => {
      if (topPromos.length === 0) return 0;
      return Math.min(prev, topPromos.length - 1);
    });
  }, [topPromos.length]);

  useEffect(() => {
    if (topPromos.length <= 1) return;
    const timer = setInterval(() => {
      setActiveTopPromoIndex((prev) => (prev + 1) % topPromos.length);
    }, 4500);
    return () => clearInterval(timer);
  }, [topPromos.length]);

  useEffect(() => {
    let active = true;
    homepageService
      .getPublicHomepage()
      .then((data: any) => {
        if (!active) return;
        const topBanners = Array.isArray(data?.topBanners)
          ? data.topBanners
          : [];
        const sliderBanners = topBanners.filter(
          (banner: any) => banner?.displayMode === "CAROUSEL",
        );
        const mappedTop: TopPromoItem[] = (
          sliderBanners.length > 0 ? sliderBanners : topBanners
        )
          .slice(0, 3)
          .map((banner: any, index: number) => ({
            id: banner?.id || `banner-${index + 1}`,
            title: banner?.title || TOP_PROMOS[index % TOP_PROMOS.length].title,
            subtitle:
              banner?.subtitle ||
              TOP_PROMOS[index % TOP_PROMOS.length].subtitle,
            bg:
              String(banner?.bannerAccentColor || "").trim() ||
              TOP_PROMO_BG_COLORS[index % TOP_PROMO_BG_COLORS.length],
            cta: banner?.ctaText || "Shop Now",
            imageUrl: banner?.imageUrl || HOME_HERO_BANNER_IMAGE_URL,
            ctaUrl: banner?.ctaUrl || "",
          }));
        const withHeroImage = mappedTop.filter((item) => Boolean(item.imageUrl));
        const nextTop =
          withHeroImage.length > 0
            ? withHeroImage
            : [
                {
                  id: "fallback-main-hero",
                  title: "Connect Indian Exporters with Global Buyers",
                  subtitle: "B2B Portal + Virtual Expo Platform",
                  bg: "#EAF2FF",
                  cta: "Explore Portal",
                  imageUrl: HOME_HERO_BANNER_IMAGE_URL,
                  ctaUrl: "/search",
                },
              ];
        if (nextTop.length > 0) {
          setTopPromos(nextTop);
          homeCache.write("topPromos", nextTop);
        }
        const bottomSource = Array.isArray(data?.bottomBanners)
          ? data.bottomBanners
          : Array.isArray(data?.rows)
            ? data.rows.flatMap((row: any) =>
                Array.isArray(row?.bannersAfter) ? row.bannersAfter : [],
              )
            : [];
        const bottomBySlot = new Map<number, any>();
        const sectionAccentByBannerId = new Map<string, string>();
        if (Array.isArray(data?.rows)) {
          data.rows.forEach((row: any) => {
            const sectionAccent = String(
              row?.section?.bannerAccentColor || "",
            ).trim();
            if (!sectionAccent || !Array.isArray(row?.bannersAfter)) return;
            row.bannersAfter.forEach((banner: any) => {
              if (banner?.id) {
                sectionAccentByBannerId.set(String(banner.id), sectionAccent);
              }
            });
          });
        }
        bottomSource.forEach((banner: any) => {
          const raw = Number(banner?.sortOrder);
          const slotIndex = Number.isInteger(raw) ? raw : -1;
          if (slotIndex >= 0 && slotIndex < 5) {
            bottomBySlot.set(slotIndex, banner);
          }
        });
        const bottomMapped: BeautyFeatureItem[] = Array.from(
          { length: 5 },
          (_, index) => {
            const banner = bottomBySlot.get(index);
            const fallback = BEAUTY_FEATURE_TILES[index];
            return {
              id: banner?.id || fallback.id,
              title: banner?.title || fallback.title,
              subtitle: banner?.subtitle || fallback.subtitle,
              cta: banner?.ctaText || fallback.cta,
              accentColor:
                String(banner?.bannerAccentColor || "").trim() ||
                (banner?.id
                  ? sectionAccentByBannerId.get(String(banner.id))
                  : "") ||
                BOTTOM_BANNER_ACCENTS[index],
              imageUrl: banner?.imageUrl || "",
              ctaUrl: banner?.ctaUrl || "",
            };
          },
        );
        setBottomFeatureTiles(bottomMapped);
        homeCache.write("bottomFeatureTiles", bottomMapped);

        const managedRows = Array.isArray(data?.rows) ? data.rows : [];
        const normalizedSections: ManagedHomepageSection[] = managedRows.map(
          (row: any, rowIndex: number) => {
            const section = row?.section || {};
            const productsRaw = Array.isArray(row?.products)
              ? row.products
              : [];
            const parsedSourceConfig = parseManagedSourceConfig(
              section?.sourceConfig ?? row?.sourceConfig ?? row?.gridConfig,
            );
            const productsObject =
              row?.products && typeof row.products === "object"
                ? row.products
                : null;
            const rawGridSectionOne =
              row?.sectionOneProducts ??
              row?.topRateProducts ??
              productsObject?.sectionOneProducts ??
              productsObject?.sectionOne ??
              productsObject?.topRate;
            const rawGridSectionTwo =
              row?.sectionTwoProducts ??
              row?.topItemsProducts ??
              productsObject?.sectionTwoProducts ??
              productsObject?.sectionTwo ??
              productsObject?.topItems;
            const gridSectionOneProducts = parseArrayProducts(
              rawGridSectionOne,
              `managed-grid-one-${rowIndex + 1}`,
            );
            const gridSectionTwoProducts = parseArrayProducts(
              rawGridSectionTwo,
              `managed-grid-two-${rowIndex + 1}`,
            );
            const rowLeftPanel =
              row?.leftPanel ??
              row?.gridLeftPanel ??
              productsObject?.leftPanel ??
              productsObject?.left ??
              null;
            const normalizedLeftPanel = {
              imageUrl:
                String(
                  rowLeftPanel?.imageUrl ??
                    rowLeftPanel?.image ??
                    parsedSourceConfig?.leftPanel?.imageUrl ??
                    "",
                ).trim() || undefined,
              title:
                String(
                  rowLeftPanel?.title ??
                    rowLeftPanel?.heading ??
                    parsedSourceConfig?.leftPanel?.title ??
                    "",
                ).trim() || undefined,
              description:
                String(
                  rowLeftPanel?.description ??
                    rowLeftPanel?.subtitle ??
                    parsedSourceConfig?.leftPanel?.description ??
                    "",
                ).trim() || undefined,
            };
            const normalizedSectionOneTitle =
              String(
                row?.sectionOneTitle ??
                  row?.topRateTitle ??
                  productsObject?.sectionOneTitle ??
                  parsedSourceConfig?.sectionOneTitle ??
                  "",
              ).trim() || undefined;
            const normalizedSectionTwoTitle =
              String(
                row?.sectionTwoTitle ??
                  row?.topItemsTitle ??
                  productsObject?.sectionTwoTitle ??
                  parsedSourceConfig?.sectionTwoTitle ??
                  "",
              ).trim() || undefined;
            const productsRawSorted = sortProductsByMembershipPlanThenVerification(
              productsRaw,
            );
            return {
              id: section?.id || `managed-section-${rowIndex + 1}`,
              title: section?.title || `Section ${rowIndex + 1}`,
              description: section?.description || "",
              publicSlug: section?.publicSlug || "",
              placement: section?.placement === "BOTTOM" ? "BOTTOM" : "TOP",
              sectionType:
                section?.sectionType === "GRID" ? "GRID" : "HORIZONTAL_SCROLL",
              bannerAccentColor:
                String(section?.bannerAccentColor || "").trim() || "#A5C7F3",
              sourceConfig: parsedSourceConfig,
              products: productsRawSorted.map((product: any, productIndex: number) =>
                mapHomepageProductToCardItem(
                  product,
                  `managed-${rowIndex + 1}`,
                  productIndex,
                ),
              ),
              gridSectionOneProducts,
              gridSectionTwoProducts,
              gridSectionOneTitle: normalizedSectionOneTitle,
              gridSectionTwoTitle: normalizedSectionTwoTitle,
              gridLeftPanel: normalizedLeftPanel,
            };
          },
        );
        const topSections = normalizedSections.filter(
          (section) => section.placement === "TOP",
        );
        const bottomSections = normalizedSections.filter(
          (section) => section.placement === "BOTTOM",
        );
        setManagedTopSections(topSections);
        setManagedBottomSections(bottomSections);
        homeCache.write("managedTopSections", topSections);
        homeCache.write("managedBottomSections", bottomSections);
        setManagedSectionPages((prev) => {
          const next: Record<string, number> = {};
          normalizedSections.forEach((section) => {
            const totalPages = Math.max(
              1,
              Math.ceil(section.products.length / 5),
            );
            next[section.id] = Math.min(prev[section.id] ?? 0, totalPages - 1);
          });
          return next;
        });
        setManagedGridSectionPages((prev) => {
          const next: Record<
            string,
            { sectionOne: number; sectionTwo: number }
          > = {};
          normalizedSections.forEach((section) => {
            next[section.id] = {
              sectionOne: Math.max(0, prev[section.id]?.sectionOne ?? 0),
              sectionTwo: Math.max(0, prev[section.id]?.sectionTwo ?? 0),
            };
          });
          return next;
        });
      })
      .catch((err) => {
        console.error("Failed to fetch top promo banners:", err);
      });

    return () => {
      active = false;
    };
  }, []);

  const handleSearch = () => {
    const trimmed = query.trim();
    if (trimmed) {
      navigate(`/search/${encodeURIComponent(trimmed)}`);
    } else {
      navigate("/search");
    }
  };

  const handleViewProduct = (id: string) => {
    navigate(getProductDetailPath(id));
  };

  const handleToggleFavorite = async (id: string, isFavorited: boolean) => {
    if (!canShowFavorites) return;
    if (favoriteLoadingIds.has(id)) return;
    setFavoriteLoadingIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    const previous = new Set(favoriteIds);
    setFavoriteIds((prev) => {
      const next = new Set(prev);
      if (isFavorited) next.delete(id);
      else next.add(id);
      return next;
    });
    try {
      if (isFavorited) {
        await favoritesService.removeFavorite(id);
      } else {
        await favoritesService.addFavorite(id);
      }
    } catch (err) {
      console.error("Failed to update favorites:", err);
      setFavoriteIds(previous);
    } finally {
      setFavoriteLoadingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleCategorySearchNavigate = (category: {
    id: string;
    name: string;
  }) => {
    const categoryPath = getCategoryPathFromId(category.id, categories);
    if (categoryPath) {
      navigate(`/search?categoryPath=${encodeURIComponent(categoryPath)}`);
      return;
    }
    navigate(`/search/${encodeURIComponent(category.name)}`);
  };

  const renderManagedSection = (
    section: ManagedHomepageSection,
    index: number,
  ) => {
    const sectionBg = section.bannerAccentColor || "#EAF2FF";
    const currentPage = managedSectionPages[section.id] ?? 0;
    const totalPages = getHomeCarouselTotalPages(section.products.length);
    if (section.sectionType === "GRID") {
      const config = section.sourceConfig ?? {};
      const sectionOneTitle =
        section.gridSectionOneTitle?.trim() ||
        config.sectionOneTitle?.trim() ||
        "Section 1";
      const sectionTwoTitle =
        section.gridSectionTwoTitle?.trim() ||
        config.sectionTwoTitle?.trim() ||
        "Section 2";
      const sectionOneIds = new Set(
        (config.sectionOneProductIds ?? []).map((id) => String(id)),
      );
      const sectionTwoIds = new Set(
        (config.sectionTwoProductIds ?? []).map((id) => String(id)),
      );
      const fromSectionOne = section.products.filter((item) =>
        sectionOneIds.has(String(item.id)),
      );
      const fromSectionTwo = section.products.filter((item) =>
        sectionTwoIds.has(String(item.id)),
      );
      const shouldUseConfiguredIds =
        fromSectionOne.length > 0 || fromSectionTwo.length > 0;
      const mid = Math.ceil(section.products.length / 2);
      const sectionOneProducts =
        section.gridSectionOneProducts &&
        section.gridSectionOneProducts.length > 0
          ? section.gridSectionOneProducts
          : shouldUseConfiguredIds
            ? fromSectionOne
            : section.products.slice(0, mid);
      const sectionTwoProducts =
        section.gridSectionTwoProducts &&
        section.gridSectionTwoProducts.length > 0
          ? section.gridSectionTwoProducts
          : shouldUseConfiguredIds
            ? fromSectionTwo
            : section.products.slice(mid);
      const sectionOnePage =
        managedGridSectionPages[section.id]?.sectionOne ?? 0;
      const sectionTwoPage =
        managedGridSectionPages[section.id]?.sectionTwo ?? 0;
      const sectionOneTotalPages = Math.max(
        1,
        Math.ceil(sectionOneProducts.length / 4),
      );
      const sectionTwoTotalPages = Math.max(
        1,
        Math.ceil(sectionTwoProducts.length / 4),
      );
      const sectionOneVisible = sectionOneProducts.slice(
        sectionOnePage * 4,
        sectionOnePage * 4 + 4,
      );
      const sectionTwoVisible = sectionTwoProducts.slice(
        sectionTwoPage * 4,
        sectionTwoPage * 4 + 4,
      );
      const leftImageUrl =
        section.gridLeftPanel?.imageUrl?.trim() ||
        config.leftPanel?.imageUrl?.trim() ||
        "";
      const leftTitle =
        section.gridLeftPanel?.title?.trim() ||
        config.leftPanel?.title?.trim() ||
        "Section Highlight";
      const leftDescription =
        section.gridLeftPanel?.description?.trim() ||
        config.leftPanel?.description?.trim() ||
        "";
      return (
        <motion.section
          key={`managed-row-${section.id}`}
          className={HOME_CAROUSEL_SECTION_MT}
          {...(shouldReduceMotion ? {} : SECTION_REVEAL)}
        >
          <div
            className="relative overflow-hidden rounded-[48px] bg-[#A5C7F3]"
            style={{ backgroundColor: sectionBg }}
          >
            <div
              className={HOME_GRID_NOTCH_CLASS}
              style={{ borderBottomRightRadius: "clamp(56px, 10vw, 84px)" }}
              aria-hidden
            />
            <div className={HOME_GRID_INNER_PAD}>
              <div className="flex flex-col gap-3 sm:min-h-[56px] sm:flex-row sm:items-start sm:justify-between">
                <div className="relative z-[3] max-w-full pr-2 sm:max-w-[min(440px,52%)]">
                  <h2 className={`${HOME_CAROUSEL_TITLE_CLASS} text-[#212529]`}>
                    {section.title}
                  </h2>
                  {section.description ? (
                    <p className={`${HOME_CAROUSEL_SUBTITLE_CLASS} text-[#637381]`}>
                      {section.description}
                    </p>
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={() =>
                    navigate(
                      `/section/${encodeURIComponent(section.publicSlug)}`,
                    )
                  }
                  className="inline-flex shrink-0 items-center gap-3 self-start rounded-full bg-white py-2.5 pl-5 pr-2 text-[13px] font-semibold text-[#212529] shadow-[0px_4px_16px_rgba(0,0,0,0.06)] sm:self-center sm:py-2"
                >
                  <span className="whitespace-nowrap">View All Products</span>
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#F97316] text-white">
                    <MoveUpRightIcon className="h-4 w-4" />
                  </span>
                </button>
              </div>
              <div className="mt-6 grid grid-cols-1 gap-5 lg:mt-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] lg:gap-6 xl:gap-8">
                <article className="rounded-[28px] bg-white p-5 shadow-[0px_8px_28px_rgba(15,52,96,0.08)] md:rounded-[32px] md:p-6">
                  <div className="relative h-[220px] overflow-hidden rounded-[22px] bg-[#eef2f7] md:h-[260px] md:rounded-[24px]">
                    <div className="flex h-full w-full items-center justify-center">
                      {leftImageUrl ? (
                        <img
                          src={leftImageUrl}
                          alt={leftTitle}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="h-[120px] w-[120px] rounded-[24px] bg-[#dfe4ec] md:h-[140px] md:w-[140px] md:rounded-[28px]" />
                      )}
                    </div>
                  </div>
                  <h3 className="mt-5 text-center text-[20px] font-bold leading-tight text-[#212529] sm:text-[22px] md:text-[24px] md:leading-[30px]">
                    {leftTitle}
                  </h3>
                  {leftDescription ? (
                    <p className="mt-2 text-center text-[13px] leading-snug text-[#637381] sm:text-sm">
                      {leftDescription}
                    </p>
                  ) : null}
                </article>
                <div className="grid gap-6 lg:grid-cols-2 lg:gap-5 xl:gap-6">
                  {[
                    {
                      key: "sectionOne",
                      title: sectionOneTitle,
                      items: sectionOneVisible,
                      page: sectionOnePage,
                      totalPages: sectionOneTotalPages,
                    },
                    {
                      key: "sectionTwo",
                      title: sectionTwoTitle,
                      items: sectionTwoVisible,
                      page: sectionTwoPage,
                      totalPages: sectionTwoTotalPages,
                    },
                  ].map((subSection) => (
                    <motion.article
                      key={`${section.id}-${subSection.key}`}
                      className="min-w-0"
                    >
                      <div className="mb-3 flex items-center justify-between gap-2">
                        <h3 className="text-[22px] font-bold text-[#212529] sm:text-[26px] md:text-[28px]">
                          {subSection.title}
                        </h3>
                        <div className="flex shrink-0 items-center gap-2">
                          <button
                            type="button"
                            onClick={() =>
                              setManagedGridSectionPages((prev) => ({
                                ...prev,
                                [section.id]: {
                                  sectionOne: prev[section.id]?.sectionOne ?? 0,
                                  sectionTwo: prev[section.id]?.sectionTwo ?? 0,
                                  [subSection.key]: Math.max(
                                    0,
                                    (prev[section.id]?.[
                                      subSection.key as
                                        | "sectionOne"
                                        | "sectionTwo"
                                    ] ?? 0) - 1,
                                  ),
                                },
                              }))
                            }
                            disabled={subSection.page === 0}
                            className={HOME_SECTION_NAV_BTN_SM}
                            aria-label="Previous products"
                          >
                            <ChevronLeft className="h-4 w-4" strokeWidth={2.25} />
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setManagedGridSectionPages((prev) => ({
                                ...prev,
                                [section.id]: {
                                  sectionOne: prev[section.id]?.sectionOne ?? 0,
                                  sectionTwo: prev[section.id]?.sectionTwo ?? 0,
                                  [subSection.key]: Math.min(
                                    subSection.totalPages - 1,
                                    (prev[section.id]?.[
                                      subSection.key as
                                        | "sectionOne"
                                        | "sectionTwo"
                                    ] ?? 0) + 1,
                                  ),
                                },
                              }))
                            }
                            disabled={
                              subSection.page >= subSection.totalPages - 1
                            }
                            className={HOME_SECTION_NAV_BTN_SM}
                            aria-label="Next products"
                          >
                            <ChevronRight className="h-4 w-4" strokeWidth={2.25} />
                          </button>
                        </div>
                      </div>
                      <motion.div
                        key={`${section.id}-${subSection.key}-p-${subSection.page}`}
                        className="space-y-3"
                        initial={shouldReduceMotion ? false : "hidden"}
                        animate={shouldReduceMotion ? undefined : "show"}
                        variants={
                          shouldReduceMotion
                            ? undefined
                            : PRODUCT_STAGGER_CONTAINER
                        }
                      >
                        {subSection.items.map((item) => (
                          <motion.div
                            key={`${section.id}-${subSection.key}-${item.id}`}
                            variants={
                              shouldReduceMotion
                                ? undefined
                                : PRODUCT_STAGGER_ITEM
                            }
                          >
                            <DealRowCard
                              id={item.id}
                              name={item.name}
                              price={item.price}
                              unitLabel={item.unitLabel}
                              oldPrice={item.oldPrice}
                              imageUrl={item.imageUrl}
                              onViewProduct={handleViewProduct}
                            />
                          </motion.div>
                        ))}
                        {subSection.items.length === 0 ? (
                          <article className="rounded-[12px] border border-dashed border-[#d7dee8] bg-white p-4 text-center text-sm text-[#637381]">
                            No products in {subSection.title}.
                          </article>
                        ) : null}
                      </motion.div>
                    </motion.article>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </motion.section>
      );
    }

    return (
      <motion.section
        key={`managed-row-${section.id}`}
        className={HOME_CAROUSEL_SECTION_MT}
        {...(shouldReduceMotion ? {} : SECTION_REVEAL)}
      >
        <div
          className={HOME_CAROUSEL_SECTION_SHELL_COMPACT_PB}
          style={{ backgroundColor: sectionBg }}
        >
          <div
            className={`${HOME_CAROUSEL_NOTCH_CLASS} bg-[#f8fbff]`}
            style={{ borderBottomRightRadius: HOME_CAROUSEL_NOTCH_RADIUS }}
            aria-hidden
          />
          <div className={HOME_CAROUSEL_HEADER_POS}>
            <h2 className={`${HOME_CAROUSEL_TITLE_CLASS} text-[#212529]`}>
              {section.title}
            </h2>
            {section.description ? (
              <p className={`${HOME_CAROUSEL_SUBTITLE_CLASS} text-[#3f4f63]`}>
                {section.description}
              </p>
            ) : null}
          </div>

          <div className={HOME_CAROUSEL_NAV_POS}>
            <HomeCarouselNavButtons
              page={currentPage}
              totalPages={totalPages}
              onPrev={() =>
                setManagedSectionPages((prev) => ({
                  ...prev,
                  [section.id]: Math.max(0, (prev[section.id] ?? 0) - 1),
                }))
              }
              onNext={() =>
                setManagedSectionPages((prev) => ({
                  ...prev,
                  [section.id]: Math.min(
                    totalPages - 1,
                    (prev[section.id] ?? 0) + 1,
                  ),
                }))
              }
            />
          </div>

          <HomePaginatedProductGrid
            products={section.products}
            carouselKey={`managed-${section.id}`}
            page={currentPage}
            canShowFavorites={canShowFavorites}
            favoriteIds={favoriteIds}
            favoriteLoadingIds={favoriteLoadingIds}
            onToggleFavorite={
              canShowFavorites ? handleToggleFavorite : undefined
            }
            onViewProduct={handleViewProduct}
            shouldReduceMotion={shouldReduceMotion}
          />

          <div className="relative z-[2] mt-3 flex justify-end">
            <button
              type="button"
              onClick={() =>
                navigate(`/section/${encodeURIComponent(section.publicSlug)}`)
              }
              className="inline-flex items-center gap-2 rounded-full bg-white py-2 pl-5 pr-2 text-sm font-semibold text-[#F97316]"
            >
              View All Products
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#F97316] text-white">
                <MoveUpRightIcon className="h-4 w-4" />
              </span>
            </button>
          </div>

          {section.products.length === 0 ? (
            <article className="relative z-[2] rounded-[12px] border border-dashed border-[#d7dee8] bg-white p-6 text-center text-sm text-[#637381]">
              No products available for this section.
            </article>
          ) : null}
        </div>
      </motion.section>
    );
  };

  return (
    <div className="home-page min-h-screen bg-[#FAFAFA]">
      <style>{`
        .home-page {
          font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", "Microsoft YaHei", "Helvetica Neue", Helvetica, Arial, sans-serif;
          color: #1A2A3D;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }
        .home-page h1,
        .home-page h2,
        .home-page h3 {
          letter-spacing: -0.01em;
        }
        .home-page article {
          transition: box-shadow 0.35s ease, transform 0.35s ease;
        }
        .home-page article:hover {
          box-shadow: 0 10px 28px rgba(26, 42, 61, 0.10);
          transform: translateY(-2px);
        }
        .home-page article img {
          transition: transform 0.5s cubic-bezier(0.22, 1, 0.36, 1);
          will-change: transform;
        }
        .home-page article:hover img {
          transform: scale(1.04);
        }
        @keyframes home-shine {
          0% { transform: translateX(-120%) skewX(-20deg); }
          100% { transform: translateX(220%) skewX(-20deg); }
        }
        .home-shine-cta {
          position: relative;
          overflow: hidden;
          isolation: isolate;
        }
        .home-shine-cta::after {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          width: 40%;
          height: 100%;
          background: linear-gradient(120deg, transparent 0%, rgba(255,255,255,0.45) 50%, transparent 100%);
          transform: translateX(-120%) skewX(-20deg);
          pointer-events: none;
          z-index: 1;
        }
        .home-shine-cta:hover::after {
          animation: home-shine 0.9s ease;
        }
        .home-product-marquee-mask {
          overflow: hidden;
        }
        .home-product-marquee-track {
          display: flex;
          width: max-content;
          gap: 0.75rem;
          animation: home-product-marquee var(--marquee-duration, 52s) linear infinite;
          will-change: transform;
        }
        .home-product-marquee-track.is-reverse {
          animation-direction: reverse;
        }
        .home-product-marquee-track:hover {
          animation-play-state: paused;
        }
        @keyframes home-product-marquee {
          from { transform: translate3d(0, 0, 0); }
          to { transform: translate3d(-50%, 0, 0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .home-page article,
          .home-page article img {
            transition: none;
          }
          .home-page article:hover {
            transform: none;
          }
          .home-page article:hover img {
            transform: none;
          }
          .home-shine-cta:hover::after {
            animation: none;
          }
          .home-product-marquee-track {
            animation: none !important;
          }
        }
      `}</style>
      <BizcoverIndiaFigmaHeader
        searchQuery={query}
        onSearchChange={setQuery}
        onSearchSubmit={handleSearch}
      />

      <motion.main
        className="mx-auto w-full max-w-[1920px] px-4 py-5 lg:px-36"
        initial={shouldReduceMotion ? false : { opacity: 0 }}
        animate={shouldReduceMotion ? undefined : { opacity: 1 }}
        transition={{ duration: 0.45, ease: EASE_OUT }}
      >
        <section className="grid gap-4 lg:grid-cols-[2.2fr_1fr] lg:items-stretch">
          <motion.article
            className={`relative w-full overflow-hidden rounded-[18px] ${
              HIDE_BANNER_TEXT_CONTENT
                ? `${HOME_TOP_BANNER_ASPECT_CLASS} min-h-[200px] lg:min-h-[360px] lg:aspect-auto lg:h-full p-0`
                : "h-full min-h-[220px] p-8 sm:min-h-[280px] lg:min-h-[360px]"
            }`}
            style={{ backgroundColor: currentTopPromo.bg || "#ffe5f4" }}
            initial={shouldReduceMotion ? false : { opacity: 0 }}
            animate={shouldReduceMotion ? undefined : { opacity: 1 }}
            transition={{ duration: 0.4, ease: EASE_OUT }}
          >
            {!HIDE_BANNER_TEXT_CONTENT ? (
              <div className="absolute inset-0 bg-gradient-to-r from-[rgba(255,255,255,0.35)] via-[rgba(255,255,255,0.14)] to-[rgba(255,255,255,0.08)]" />
            ) : null}
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={currentTopPromo.id}
                className={
                  HIDE_BANNER_TEXT_CONTENT
                    ? "absolute inset-0 flex items-center justify-center"
                    : "relative"
                }
                initial={shouldReduceMotion ? false : { opacity: 0 }}
                animate={shouldReduceMotion ? undefined : { opacity: 1 }}
                exit={shouldReduceMotion ? undefined : { opacity: 0 }}
                transition={{ duration: 0.35, ease: EASE_OUT }}
              >
                {currentTopPromo.imageUrl ? (
                  HIDE_BANNER_TEXT_CONTENT ? (
                    <img
                      src={currentTopPromo.imageUrl}
                      alt={currentTopPromo.title}
                      className="block h-full w-full max-h-full max-w-full object-contain object-center"
                      loading="lazy"
                      decoding="async"
                    />
                  ) : (
                    <img
                      src={currentTopPromo.imageUrl}
                      alt={currentTopPromo.title}
                      className="pointer-events-none absolute right-0 top-1/2 h-[88%] w-[45%] max-h-full max-w-[45%] -translate-y-1/2 object-contain object-center opacity-95"
                      loading="lazy"
                      decoding="async"
                    />
                  )
                ) : null}
                {!HIDE_BANNER_TEXT_CONTENT ? (
                  <>
                    <span className="rounded bg-[#9cf06a] px-2 py-1 text-[10px] font-bold uppercase text-[#16362d]">
                      25% Off
                    </span>
                    <span className="ml-2 text-sm font-semibold text-[#d93f93]">
                      Exclusive offer
                    </span>
                    <h2 className="mt-4 max-w-[520px] text-[42px] font-bold leading-[46px] text-[#17212b] [text-shadow:0_1px_2px_rgba(255,255,255,0.3)]">
                      {currentTopPromo.title}
                    </h2>
                    <p className="mt-3 max-w-[420px] text-[15px] text-[#2c3b49]">
                      {currentTopPromo.subtitle}
                    </p>
                    <motion.button
                      whileHover={
                        shouldReduceMotion ? undefined : { scale: 1.04, y: -1 }
                      }
                      whileTap={
                        shouldReduceMotion ? undefined : { scale: 0.98 }
                      }
                      onClick={() => {
                        const ctaUrl = currentTopPromo.ctaUrl;
                        if (!ctaUrl) return;
                        if (ctaUrl.startsWith("http")) {
                          window.open(ctaUrl, "_blank", "noopener,noreferrer");
                        } else {
                          navigate(ctaUrl);
                        }
                      }}
                      className="home-shine-cta mt-5 rounded-full bg-[#F97316] px-5 py-2 text-[13px] font-semibold text-white hover:bg-[#EA580C]"
                    >
                      {currentTopPromo.cta || "Shop Now"}
                    </motion.button>
                  </>
                ) : null}
              </motion.div>
            </AnimatePresence>

            <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  if (topPromos.length <= 1) return;
                  setActiveTopPromoIndex(
                    (prev) => (prev - 1 + topPromos.length) % topPromos.length,
                  );
                }}
                className={HOME_SECTION_NAV_BTN_XS}
                aria-label="Previous slide"
              >
                <ChevronLeft className="h-3.5 w-3.5" strokeWidth={2.25} />
              </button>
              <div className="flex items-center gap-2 rounded-t-[28px] bg-white px-5 py-2">
                {topPromos.map((promo, index) => (
                  <button
                    key={promo.id}
                    onClick={() => setActiveTopPromoIndex(index)}
                    className={`h-2.5 rounded-full transition-all ${
                      index === activeTopPromoIndex
                        ? "w-10 bg-[#F97316]"
                        : "w-2.5 bg-[#d2dbe4]"
                    }`}
                  />
                ))}
              </div>
              <button
                type="button"
                onClick={() => {
                  if (topPromos.length <= 1) return;
                  setActiveTopPromoIndex(
                    (prev) => (prev + 1) % topPromos.length,
                  );
                }}
                className={HOME_SECTION_NAV_BTN_XS}
                aria-label="Next slide"
              >
                <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.25} />
              </button>
            </div>
          </motion.article>

          <HomeHeroSideCtaStack
            onJoinSupplier={() => navigate("/login?tab=signup")}
            onPostBuyingRequest={() => navigate("/login?tab=signup")}
          />
        </section>

        <motion.section
          className={HOME_CAROUSEL_SECTION_MT}
          {...(shouldReduceMotion ? {} : SECTION_REVEAL)}
        >
          <div
            className={`${HOME_CAROUSEL_SECTION_SHELL} !pt-4 sm:!pt-5 md:!pt-6`}
            style={{
              backgroundColor: SHOP_BY_CATEGORY_BG,
              boxShadow: HOME_SECTION_SHADOW,
            }}
          >
            <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2
                  className={HOME_CAROUSEL_TITLE_CLASS}
                  style={{ color: SHOP_BY_CATEGORY_HEADING }}
                >
                  Product Categories
                </h2>
                <p
                  className={HOME_CAROUSEL_SUBTITLE_CLASS}
                  style={{ color: SHOP_BY_CATEGORY_SUBTITLE }}
                >
                  Browse products by category
                </p>
              </div>
              {categorySliderTotalPages > 1 && (
                <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() =>
                    setCategorySliderPage((prev) => Math.max(0, prev - 1))
                  }
                  disabled={categorySliderPage === 0}
                  className={HOME_SECTION_NAV_BTN_MD}
                  aria-label="Previous categories"
                >
                  <ChevronLeft className="h-4 w-4" strokeWidth={2.25} />
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setCategorySliderPage((prev) =>
                      Math.min(categorySliderTotalPages - 1, prev + 1),
                    )
                  }
                  disabled={categorySliderPage >= categorySliderTotalPages - 1}
                  className={HOME_SECTION_NAV_BTN_MD}
                  aria-label="Next categories"
                >
                  <ChevronRight className="h-4 w-4" strokeWidth={2.25} />
                </button>
                </div>
              )}
            </div>

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8">
            {visibleCategorySlides.map((category) => (
              <motion.button
                key={category.id}
                type="button"
                onClick={() => handleCategorySearchNavigate(category)}
                className="group flex flex-col items-center text-center"
                initial={shouldReduceMotion ? false : { opacity: 0 }}
                animate={shouldReduceMotion ? undefined : { opacity: 1 }}
                transition={{
                  duration: 0.36,
                  delay: 0,
                  ease: EASE_OUT,
                }}
                whileTap={shouldReduceMotion ? undefined : { scale: 0.98 }}
              >
                <span className="relative flex h-[106px] w-[106px] items-center justify-center overflow-hidden rounded-full border border-[#e6ebf2] bg-[#f7f9fc] shadow-sm transition-transform duration-200 group-hover:scale-[1.03]">
                  <span className="absolute h-[38px] w-[38px] rounded-[10px] border border-[#b8c2cf] bg-[#dfe4ec]" />
                  <img
                    src={getHomeProductCategoryImageUrl(
                      category,
                      categoryImageById,
                    )}
                    alt={category.name}
                    className="relative h-[100px] w-[100px] rounded-full object-cover"
                    loading="lazy"
                    onError={(e) => {
                      const target = e.currentTarget as HTMLImageElement;
                      const lf = getLoremFlickrFallbackUrl(category.name);
                      if (target.src !== lf) {
                        target.src = lf;
                      } else {
                        target.style.display = "none";
                      }
                    }}
                  />
                </span>
                <span
                  className="mt-3 line-clamp-1 text-[15px] font-semibold leading-5"
                  style={{ color: SHOP_BY_CATEGORY_LABEL }}
                >
                  {category.name}
                </span>
              </motion.button>
            ))}
            {visibleCategorySlides.length === 0 && (
              <div className="col-span-full rounded-[12px] border border-dashed border-[#d7dee8] bg-white p-6 text-center text-sm text-[#637381]">
                No categories available.
              </div>
            )}
          </div>
          </div>
        </motion.section>

        {managedTopSections.map((section, index) =>
          renderManagedSection(section, index),
        )}

        {/* <section className="mt-10">
          <h2 className="text-center text-[30px] font-bold leading-[36px] text-[#212529]">Shop By Brands</h2>
          <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-6">
            {liveBrands.map((brand) => (
              <article key={brand} className="rounded-[12px] border border-[#edf1f7] bg-white p-4">
                <div className="flex h-[72px] items-center justify-center">
                  <span className="text-center text-[18px] font-bold tracking-[0.01em] text-[#212529]">{brand}</span>
                </div>
              </article>
            ))}
            {liveBrands.length === 0 && (
              <article className="col-span-full rounded-[12px] border border-dashed border-[#d7dee8] bg-white p-6 text-center text-sm text-[#637381]">
                No brand data available from live products yet.
              </article>
            )}
          </div>
        </section> */}

        {/* <section className="mt-10">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-[30px] font-bold leading-[36px] text-[#212529]">Category Favorites</h2>
            <div className="flex items-center gap-2">
              <button className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#f3f6fb] text-[#5f6b7a]">
                {"<"}
              </button>
              <button className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#0c8e87] text-white">
                {">"}
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            {CATEGORY_FAVORITES.map((item) => (
              <article key={item.title} className="overflow-hidden rounded-[18px] border border-[#edf1f7] bg-white">
                <div className="flex h-[260px] flex-col items-center justify-center bg-[#f7fbff]">
                  <div className="h-[88px] w-[88px] rounded-[20px] bg-[#dfe4ec]" />
                  <p className="mt-3 text-[12px] text-[#a5b0bf]">No Image Preview</p>
                </div>
                <div className={`px-4 py-5 text-center ${item.bg}`}>
                  <h3 className="text-[24px] font-bold leading-[28px] text-[#112138]">{item.title}</h3>
                  <p className="mt-2 text-[14px] font-semibold text-[#112138]">{item.count}</p>
                </div>
              </article>
            ))}
          </div>
        </section> */}
        <motion.section
          className={HOME_CAROUSEL_SECTION_MT}
          {...(shouldReduceMotion ? {} : SECTION_REVEAL)}
        >
          <div
            className={HOME_CAROUSEL_SECTION_SHELL}
            style={{
              backgroundColor: NEW_ARRIVALS_BG,
              boxShadow: HOME_SECTION_SHADOW,
            }}
            onMouseEnter={() => setPauseNewArrivalsAuto(true)}
            onMouseLeave={() => setPauseNewArrivalsAuto(false)}
          >
            <div
              className={HOME_CAROUSEL_NOTCH_CLASS}
              style={{
                borderBottomRightRadius: HOME_CAROUSEL_NOTCH_RADIUS,
                backgroundColor: NEW_ARRIVALS_NOTCH,
              }}
              aria-hidden
            />
            <div className={HOME_CAROUSEL_HEADER_POS}>
              <h2
                className={HOME_CAROUSEL_TITLE_CLASS}
                style={{ color: NEW_ARRIVALS_HEADING }}
              >
                New Arrivals
              </h2>
              <p
                className={HOME_CAROUSEL_SUBTITLE_CLASS}
                style={{ color: NEW_ARRIVALS_SUBTITLE }}
              >
                Freshly added products curated for you
              </p>
            </div>
            <div className={HOME_CAROUSEL_NAV_POS}>
              <HomeCarouselNavButtons
                page={beautyPage}
                totalPages={beautyTotalPages}
                onPrev={() => setBeautyPage((prev) => Math.max(0, prev - 1))}
                onNext={() =>
                  setBeautyPage((prev) =>
                    Math.min(beautyTotalPages - 1, prev + 1),
                  )
                }
              />
            </div>

            {launchProducts.length === 0 ? (
              <div className="relative z-[2] mt-2 rounded-[14px] border border-dashed border-[#d7dee8] bg-white/80 p-5 text-center text-sm text-[#637381]">
                No new arrivals available yet.
              </div>
            ) : (
              <HomePaginatedProductGrid
                products={launchProducts}
                carouselKey="new-arrivals"
                page={beautyPage}
                canShowFavorites={canShowFavorites}
                favoriteIds={favoriteIds}
                favoriteLoadingIds={favoriteLoadingIds}
                onToggleFavorite={
                  canShowFavorites ? handleToggleFavorite : undefined
                }
                onViewProduct={handleViewProduct}
                shouldReduceMotion={shouldReduceMotion}
              />
            )}
          </div>
        </motion.section>

        <motion.section
          className={HOME_CAROUSEL_SECTION_MT}
          {...(shouldReduceMotion ? {} : SECTION_REVEAL)}
        >
          <div
            className={HOME_CAROUSEL_SECTION_SHELL}
            style={{
              backgroundColor: NEW_ARRIVALS_BG,
              boxShadow: HOME_SECTION_SHADOW,
            }}
            onMouseEnter={() => setPauseFeaturedAuto(true)}
            onMouseLeave={() => setPauseFeaturedAuto(false)}
          >
            <div
              className={HOME_CAROUSEL_NOTCH_CLASS}
              style={{
                borderBottomRightRadius: HOME_CAROUSEL_NOTCH_RADIUS,
                backgroundColor: NEW_ARRIVALS_NOTCH,
              }}
              aria-hidden
            />
            <div className={HOME_CAROUSEL_HEADER_POS}>
              <h2
                className={HOME_CAROUSEL_TITLE_CLASS}
                style={{ color: NEW_ARRIVALS_HEADING }}
              >
                Featured Products
              </h2>
              <p
                className={HOME_CAROUSEL_SUBTITLE_CLASS}
                style={{ color: NEW_ARRIVALS_SUBTITLE }}
              >
                Seller-highlighted picks from across the marketplace
              </p>
            </div>
            <div className={HOME_CAROUSEL_NAV_POS}>
              <HomeCarouselNavButtons
                page={featuredPage}
                totalPages={featuredTotalPages}
                onPrev={() => setFeaturedPage((prev) => Math.max(0, prev - 1))}
                onNext={() =>
                  setFeaturedPage((prev) =>
                    Math.min(featuredTotalPages - 1, prev + 1),
                  )
                }
              />
            </div>

            {featuredProducts.length === 0 ? (
              <div className="relative z-[2] mt-2 rounded-[14px] border border-dashed border-[#d7dee8] bg-white/80 p-5 text-center text-sm text-[#637381]">
                No featured products available yet.
              </div>
            ) : (
              <HomePaginatedProductGrid
                products={featuredProducts}
                carouselKey="featured-products"
                page={featuredPage}
                canShowFavorites={canShowFavorites}
                favoriteIds={favoriteIds}
                favoriteLoadingIds={favoriteLoadingIds}
                onToggleFavorite={
                  canShowFavorites ? handleToggleFavorite : undefined
                }
                onViewProduct={handleViewProduct}
                shouldReduceMotion={shouldReduceMotion}
              />
            )}
          </div>
        </motion.section>

        {SHOW_HOME_MIDDLE_BANNERS && (
        <motion.section
          className="mt-8"
          {...(shouldReduceMotion ? {} : SECTION_REVEAL)}
        >
          <div className="grid gap-4 lg:grid-cols-3">
            {bottomFeatureTiles.slice(0, 3).map((tile) => (
              <motion.div
                key={tile.id}
                initial={shouldReduceMotion ? false : { opacity: 0 }}
                whileInView={
                  shouldReduceMotion ? undefined : { opacity: 1 }
                }
                viewport={
                  shouldReduceMotion ? undefined : { once: true, amount: 0.15 }
                }
                transition={{
                  duration: 0.36,
                  delay: 0,
                  ease: EASE_OUT,
                }}
              >
                <BeautyFeatureCard
                  title={tile.title}
                  subtitle={tile.subtitle}
                  cta={tile.cta}
                  accentColor={tile.accentColor}
                  imageUrl={tile.imageUrl}
                  onCtaClick={() => {
                    if (tile.ctaUrl?.startsWith("http")) {
                      window.open(tile.ctaUrl, "_blank", "noopener,noreferrer");
                    } else if (tile.ctaUrl) {
                      navigate(tile.ctaUrl);
                    } else {
                      navigate("/search");
                    }
                  }}
                />
              </motion.div>
            ))}
          </div>
          {/* <div className="mt-4 grid grid-cols-2 gap-3 rounded-[14px] bg-white/60 p-3 sm:grid-cols-3 lg:grid-cols-6">
           {BEAUTY_LOGOS.map((logo) => (
             <div key={logo} className="flex items-center justify-center">
               <div className="flex h-[56px] w-[56px] items-center justify-center rounded-full border border-[#111827] text-[8px] font-semibold tracking-[0.14em] text-[#111827]">
                 {logo.split(" ").map((token) => token[0]).join("")}
               </div>
             </div>
           ))}
         </div> */}

          <div className="mt-4 grid gap-4 lg:grid-cols-[1fr_2fr]">
            <motion.div
              initial={shouldReduceMotion ? false : { opacity: 0 }}
              whileInView={
                shouldReduceMotion ? undefined : { opacity: 1 }
              }
              viewport={
                shouldReduceMotion ? undefined : { once: true, amount: 0.15 }
              }
              transition={{ duration: 0.36, ease: EASE_OUT }}
            >
              <BeautyFeatureCard
                title={
                  bottomFeatureTiles[3]?.title || BEAUTY_FEATURE_TILES[3].title
                }
                subtitle={
                  bottomFeatureTiles[3]?.subtitle ||
                  BEAUTY_FEATURE_TILES[3].subtitle
                }
                cta={bottomFeatureTiles[3]?.cta || BEAUTY_FEATURE_TILES[3].cta}
                accentColor={
                  bottomFeatureTiles[3]?.accentColor || BOTTOM_BANNER_ACCENTS[3]
                }
                imageUrl={bottomFeatureTiles[3]?.imageUrl || ""}
                tall
                onCtaClick={() => {
                  const ctaUrl = bottomFeatureTiles[3]?.ctaUrl || "";
                  if (ctaUrl.startsWith("http")) {
                    window.open(ctaUrl, "_blank", "noopener,noreferrer");
                  } else if (ctaUrl) {
                    navigate(ctaUrl);
                  } else {
                    navigate("/search");
                  }
                }}
              />
            </motion.div>
            <motion.div
              initial={shouldReduceMotion ? false : { opacity: 0 }}
              whileInView={
                shouldReduceMotion ? undefined : { opacity: 1 }
              }
              viewport={
                shouldReduceMotion ? undefined : { once: true, amount: 0.15 }
              }
              transition={{
                duration: 0.36,
                delay: 0,
                ease: EASE_OUT,
              }}
            >
              <BeautyFeatureCard
                title={
                  bottomFeatureTiles[4]?.title || BEAUTY_FEATURE_TILES[4].title
                }
                subtitle={
                  bottomFeatureTiles[4]?.subtitle ||
                  BEAUTY_FEATURE_TILES[4].subtitle
                }
                cta={bottomFeatureTiles[4]?.cta || BEAUTY_FEATURE_TILES[4].cta}
                accentColor={
                  bottomFeatureTiles[4]?.accentColor || BOTTOM_BANNER_ACCENTS[4]
                }
                imageUrl={bottomFeatureTiles[4]?.imageUrl || ""}
                tall
                onCtaClick={() => {
                  const ctaUrl = bottomFeatureTiles[4]?.ctaUrl || "";
                  if (ctaUrl.startsWith("http")) {
                    window.open(ctaUrl, "_blank", "noopener,noreferrer");
                  } else if (ctaUrl) {
                    navigate(ctaUrl);
                  } else {
                    navigate("/search");
                  }
                }}
              />
            </motion.div>
          </div>
        </motion.section>
        )}

        {industryShowcaseRows.map((row) => (
          <motion.section
            key={`industry-${row.rowKey}`}
            className={HOME_CAROUSEL_SECTION_MT}
            {...(shouldReduceMotion ? {} : SECTION_REVEAL)}
          >
            <div
              className={HOME_CAROUSEL_SECTION_SHELL}
              style={{
                backgroundColor: INDUSTRY_ROW_BG[row.rowKey] ?? "#EAF2FF",
                boxShadow: HOME_SECTION_SHADOW,
              }}
              onMouseEnter={() => setPausedIndustryCarouselKey(row.rowKey)}
              onMouseLeave={() => setPausedIndustryCarouselKey(null)}
            >
              <div
                className={HOME_CAROUSEL_NOTCH_CLASS}
                style={{
                  borderBottomRightRadius: HOME_CAROUSEL_NOTCH_RADIUS,
                  backgroundColor:
                    INDUSTRY_ROW_NOTCH[row.rowKey] ?? "#f8fbff",
                }}
                aria-hidden
              />
              <div className={HOME_CAROUSEL_HEADER_POS_WIDE}>
                <h2
                  className={HOME_CAROUSEL_TITLE_CLASS}
                  style={{
                    color:
                      INDUSTRY_ROW_HEADING[row.rowKey] ?? "#212529",
                  }}
                >
                  {row.title}
                </h2>
                <p
                  className={HOME_CAROUSEL_SUBTITLE_CLASS}
                  style={{
                    color:
                      INDUSTRY_ROW_SUBTITLE[row.rowKey] ?? "#3f4f63",
                  }}
                >
                  Featured products in this category
                </p>
              </div>
              <div className={HOME_CAROUSEL_NAV_POS}>
                <HomeCarouselNavButtons
                  page={industrySectionPages[row.rowKey] ?? 0}
                  totalPages={getHomeCarouselTotalPages(row.products.length)}
                  onPrev={() =>
                    setIndustrySectionPages((prev) => ({
                      ...prev,
                      [row.rowKey]: Math.max(0, (prev[row.rowKey] ?? 0) - 1),
                    }))
                  }
                  onNext={() =>
                    setIndustrySectionPages((prev) => ({
                      ...prev,
                      [row.rowKey]: Math.min(
                        getHomeCarouselTotalPages(row.products.length) - 1,
                        (prev[row.rowKey] ?? 0) + 1,
                      ),
                    }))
                  }
                />
              </div>

              {industryShowcaseLoading && row.products.length === 0 ? (
                <div className="relative z-[2] mt-2 flex min-h-[120px] items-center justify-center rounded-[14px] border border-dashed border-[#d7dee8] bg-white/70 text-sm text-[#637381]">
                  Loading products…
                </div>
              ) : null}
              {!industryShowcaseLoading &&
              catalogCategoriesLoaded &&
              !row.navigateCategoryId &&
              row.products.length === 0 ? (
                <div className="relative z-[2] mt-2 rounded-[14px] border border-dashed border-[#d7dee8] bg-white/80 p-5 text-center text-sm text-[#637381]">
                  No matching category in the catalog for this industry.
                </div>
              ) : null}
              {!industryShowcaseLoading &&
              row.navigateCategoryId &&
              row.products.length === 0 ? (
                <div className="relative z-[2] mt-2 rounded-[14px] border border-dashed border-[#d7dee8] bg-white/80 p-5 text-center text-sm text-[#637381]">
                  No products in this category yet.
                </div>
              ) : null}

              {row.products.length > 0 ? (
                <HomePaginatedProductGrid
                  products={row.products}
                  carouselKey={`industry-${row.rowKey}`}
                  page={industrySectionPages[row.rowKey] ?? 0}
                  canShowFavorites={canShowFavorites}
                  favoriteIds={favoriteIds}
                  favoriteLoadingIds={favoriteLoadingIds}
                  onToggleFavorite={
                    canShowFavorites ? handleToggleFavorite : undefined
                  }
                  onViewProduct={handleViewProduct}
                  shouldReduceMotion={shouldReduceMotion}
                />
              ) : null}
            </div>
          </motion.section>
        ))}

        {SHOW_HOME_FEATURED_AND_BEST_SELLING && (
          <motion.section
            className="mt-10 rounded-[18px] bg-[#eef7ff] px-4 py-6 md:px-8"
            {...(shouldReduceMotion ? {} : SECTION_REVEAL)}
          >
            <h2 className="text-center text-[30px] font-bold leading-[36px] text-[#212529]">
              Best Selling
            </h2>
            <p className="mt-1 text-center text-sm text-[#3f4f63]">
              Don&apos;t miss out-grab these best-selling picks!
            </p>
            <div className="mx-auto mt-6 grid max-w-[1720px] grid-cols-1 items-stretch gap-4 md:grid-cols-3 xl:grid-cols-5">
              {bestSellingPreview.map((item) => (
                <motion.div
                  key={`best-selling-${item.id}`}
                  className="h-full"
                  initial={shouldReduceMotion ? false : { opacity: 0 }}
                  whileInView={
                    shouldReduceMotion ? undefined : { opacity: 1 }
                  }
                  viewport={
                    shouldReduceMotion
                      ? undefined
                      : { once: true, amount: 0.05 }
                  }
                  transition={{
                    duration: 0.36,
                    delay: 0,
                    ease: EASE_OUT,
                  }}
                >
                  <ProductCard
                    id={item.id}
                    name={item.name}
                    category={item.category}
                    price={item.price}
                    unitLabel={item.unitLabel}
                    oldPrice={item.oldPrice}
                    imageUrl={item.imageUrl}
                    onViewProduct={handleViewProduct}
                    isFavorited={
                      canShowFavorites
                        ? favoriteIds.has(String(item.id))
                        : false
                    }
                    isFavoriteLoading={
                      canShowFavorites
                        ? favoriteLoadingIds.has(String(item.id))
                        : false
                    }
                    onToggleFavorite={
                      canShowFavorites ? handleToggleFavorite : undefined
                    }
                  />
                </motion.div>
              ))}
              {bestSellingPreview.length === 0 && (
                <article className="col-span-full rounded-[12px] border border-dashed border-[#d7dee8] bg-white p-6 text-center text-sm text-[#637381]">
                  No best selling products available.
                </article>
              )}
            </div>
          </motion.section>
        )}

        {SHOW_HOME_FEATURED_AND_BEST_SELLING && (
          <motion.section
            className="mt-10 rounded-[18px] bg-[#EEF6FF] px-4 py-6 md:px-8"
            {...(shouldReduceMotion ? {} : SECTION_REVEAL)}
          >
            <h2 className="text-center text-[30px] font-bold leading-[36px] text-[#212529]">
              Featured Products
            </h2>
            <div className="mx-auto mt-6 grid max-w-[1720px] grid-cols-1 items-stretch gap-4 md:grid-cols-3 xl:grid-cols-5">
              {featuredProducts.map((item) => (
                <motion.div
                  key={item.id}
                  className="h-full"
                  initial={shouldReduceMotion ? false : { opacity: 0 }}
                  whileInView={
                    shouldReduceMotion ? undefined : { opacity: 1 }
                  }
                  viewport={
                    shouldReduceMotion
                      ? undefined
                      : { once: true, amount: 0.05 }
                  }
                  transition={{
                    duration: 0.36,
                    delay: 0,
                    ease: EASE_OUT,
                  }}
                >
                  <ProductCard
                    id={item.id}
                    name={item.name}
                    category={item.category}
                    price={item.price}
                    unitLabel={item.unitLabel}
                    oldPrice={item.oldPrice}
                    imageUrl={item.imageUrl}
                    onViewProduct={handleViewProduct}
                    isFavorited={
                      canShowFavorites
                        ? favoriteIds.has(String(item.id))
                        : false
                    }
                    isFavoriteLoading={
                      canShowFavorites
                        ? favoriteLoadingIds.has(String(item.id))
                        : false
                    }
                    onToggleFavorite={
                      canShowFavorites ? handleToggleFavorite : undefined
                    }
                  />
                </motion.div>
              ))}
              {featuredProducts.length === 0 && (
                <article className="col-span-full rounded-[12px] border border-dashed border-[#d7dee8] bg-white p-6 text-center text-sm text-[#637381]">
                  No featured products available.
                </article>
              )}
            </div>
          </motion.section>
        )}

        {managedBottomSections.map((section, index) =>
          renderManagedSection(section, index),
        )}
      </motion.main>

      <BizcoverIndiaFigmaFooter
        email={newsletterEmail}
        onEmailChange={setNewsletterEmail}
        onSubscribe={() => {
          // Keep behavior isolated for preview page.
        }}
      />
    </div>
  );
}
